import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import { PDFDocument } from "pdf-lib";
import { showToast } from "../components/ui/custom-toast";
import {
  formatVersion,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import {
  Search,
  Plus,
  RefreshCw,
  Loader2,
  Trash2,
  ArrowLeft,
  FileText,
  Upload,
  Download,
  Send,
  ShieldCheck,
  XCircle,
  Ban,
  AlertTriangle,
  Calendar,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Save,
  X,
  File,
  CheckCircle,
  Eye,
  Power,
  Tag,
  FolderOpen,
  FilePlus,
  History,
  User,
  Building2,
} from "lucide-react";
import { DataTable, Column, ViewModeToggle } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { StatusBadge } from "../components/StatusBadge";
import {
  MetaDates,
  TitleTooltip,
  VersionChip,
  statusAccent,
} from "../components/EntityCardGrid";
import { SkeletonList, SkeletonCard } from "../components/Skeleton";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  Modal,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { GovernanceUploadModal } from "./GovernanceUploadModal";
import { getAPI } from "../../api/frameworksApi";
import { getErrorMessage } from "../../api/errorMap";
import { Tip } from "../components/ui/tooltip";
import { API_URL } from "../../constants";
import { useAuthStore, type AuthUser } from "../../stores/authStore";
import { RBACButton, RBACAction } from "../components/RBACButton";
import {
  listGovernanceDocuments,
  getGovernanceDocument,
  createGovernanceDocument,
  updateGovernanceDocument,
  deleteGovernanceDocument,
  deleteGovernanceDocumentFile,
  uploadGovernanceDocumentFile,
  downloadGovernanceDocumentFile,
  previewGovernanceDocumentFile,
  submitGovernanceDocumentForReview,
  reviewGovernanceDocument,
  createNewGovernanceDocumentVersion,
  retireGovernanceDocument,
  getGovernanceDocumentHistory,
  downloadGovernanceDocumentVersionFile,
  listSupportingEvidence,
  getSupportingEvidence,
  createSupportingEvidence,
  updateSupportingEvidence,
  deleteSupportingEvidence,
  uploadSupportingEvidenceFile,
  downloadSupportingEvidenceFile,
  previewSupportingEvidenceFile,
  searchEvidenceTags,
  validateGovernanceDocumentFile,
  deleteGovernanceDocumentOrphan,
  ApiEnvelopeError,
  type GovernanceDocumentSummaryResponse,
  type GovernanceDocumentResponse,
  type GovernanceDocumentType,
  type DocumentLifecycleStatus,
  type SupportingEvidence,
  type SupportingEvidenceResponse,
  type VersionInfo,
  type HistoryEvent,
  type ValidateFileResult,
} from "../../api/frameworksApi";
import {
  ValidationResultPanel,
  isValidationPassed,
} from "../components/ValidationResultPanel";
import { ValidationProgress } from "../components/ValidationProgress";

/* ── Authenticated download helper ── */
async function triggerDownload(url: string, fileName?: string | null) {
  const token = useAuthStore.getState().getToken();
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // The success path streams a file blob; error responses come back as the
      // standard JSON error envelope. Surface the real API message (e.g. a
      // permission-denied detail) instead of a generic "Download failed".
      let parsed: unknown = null;
      try {
        parsed = await res.json();
      } catch {
        /* non-JSON error body — fall back to the generic message */
      }
      const title =
        (parsed as { errors?: { title?: string }[] })?.errors?.[0]?.title ||
        "Download failed";
      showToast("error", getErrorMessage(parsed, "Download failed"), title);
      return;
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const serverName =
      disposition
        .match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)?.[1]
        ?.trim() ?? "";
    const name = fileName || serverName || "download";
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Download failed";
    showToast("error", msg || "Download failed");
  }
}

/* ── Constants ── */
const DOCUMENT_TYPES: { value: GovernanceDocumentType; label: string }[] = [
  { value: "POLICY", label: "Policy" },
  { value: "STANDARD", label: "Standard" },
  { value: "GUIDELINE", label: "Guideline" },
  { value: "PROCEDURE", label: "Procedure" },
];

const DOC_TYPE_COLORS: Record<string, string> = {
  POLICY:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200/60 dark:ring-blue-500/20",
  STANDARD:
    "bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 ring-1 ring-teal-200/60 dark:ring-teal-500/20",
  GUIDELINE:
    "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/60 dark:ring-orange-500/20",
  PROCEDURE:
    "bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 ring-1 ring-fuchsia-200/60 dark:ring-fuchsia-500/20",
};

const LIFECYCLE_STATUSES: { value: DocumentLifecycleStatus; label: string }[] =
  [
    { value: "DRAFT", label: "Draft" },
    { value: "IN_REVIEW", label: "In Review" },
    { value: "APPROVED", label: "Approved" },
    { value: "PUBLISHED", label: "Published" },
    { value: "RETIRED", label: "Withdrawn" },
  ];

const TITLE_MAX_LENGTH = 150;
const EVIDENCE_TITLE_MAX = 100;
const DAYS_MAX = 366;
// Max upload size (25 MB) — keep in sync with the "max 25 MB" hint text.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/* ── UI row type for governance documents table ── */
interface GovDocRow {
  [key: string]: unknown;
  id: string;
  title: string;
  documentType: string;
  status: string;
  currentVersion: number;
  versionCount: number;
  reviewDate: string;
  createdAt: string;
  updatedAt: string;
  daysUntilReview: number | null;
  reviewDueSoon: boolean;
  hasFile: boolean;
}

/* ── UI row type for evidence table ── */
interface EvidenceRow {
  [key: string]: unknown;
  id: string;
  title: string;
  description: string;
  tags: string[];
  hasFile: boolean;
  fileName: string | null;
  fileSize: number | null;
  contentType: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Map backend lifecycle status to StatusBadge variant */
function mapLifecycleToVariant(
  s: string,
): "draft" | "in_review" | "approved" | "published" | "review_overdue" | "retired" {
  const map: Record<
    string,
    "draft" | "in_review" | "approved" | "published" | "review_overdue" | "retired"
  > = {
    DRAFT: "draft",
    IN_REVIEW: "in_review",
    APPROVED: "approved",
    PUBLISHED: "published",
    REVIEW_OVERDUE: "review_overdue",
    RETIRED: "retired",
  };
  return map[s] ?? "draft";
}

function formatDate(d: string | null): string {
  if (!d) return "-";
  return d.split("T")[0].split("-").reverse().join("/");
}

function formatDateTime(d: string | null): string {
  if (!d) return "-";
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${day}/${month}/${year} ${hour12}:${minutes} ${ampm}`;
}

function formatDocType(t: string): string {
  return DOCUMENT_TYPES.find((d) => d.value === t)?.label ?? t;
}

/**
 * Validate a title field. Returns an error message, or null when valid.
 * Rejects junk such as a single character repeated (e.g. "HHHHHH").
 */
function getTitleValidationError(raw: string): string | null {
  const title = raw.trim();
  if (!title) return null; // emptiness is handled by the required/disabled state
  if (title.length < 3) return "Title must be at least 3 characters.";
  if (!/[a-zA-Z0-9]/.test(title))
    return "Title must contain letters or numbers.";
  // Distinct non-space characters — a single repeated character has only 1.
  const distinct = new Set(title.replace(/\s+/g, "").toLowerCase()).size;
  if (distinct < 2)
    return "Enter a meaningful title, not a single repeated character.";
  return null;
}

function toGovDocRow(d: GovernanceDocumentSummaryResponse): GovDocRow {
  return {
    id: d.id,
    title: d.title,
    documentType: d.documentType,
    status: d.status,
    currentVersion: d.currentVersion,
    versionCount: d.versionCount,
    reviewDate: formatDate(d.reviewDate),
    createdAt: formatDate(d.createdAt),
    updatedAt: formatDate(d.updatedAt),
    daysUntilReview: d.daysUntilReview,
    reviewDueSoon: d.reviewDueSoon ?? false,
    hasFile: d.hasFile,
  };
}

function toEvidenceRow(d: SupportingEvidence): EvidenceRow {
  return {
    id: d.id,
    title: d.title,
    description: "",
    tags: d.tags,
    hasFile: d.hasFile,
    fileName: d.fileName,
    fileSize: d.fileSize,
    contentType: d.contentType,
    createdAt: formatDate(d.createdAt),
    updatedAt: formatDate(d.updatedAt),
  };
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getAuthUserDisplayName(user: AuthUser | null): string | null {
  if (!user) return null;
  return (
    user.displayName?.trim() ||
    user.username?.trim() ||
    user.email?.trim() ||
    null
  );
}

function getHistoryActorName(
  event: HistoryEvent,
  currentUser: AuthUser | null,
): string {
  const actorName = event.performedByName?.trim();
  const currentUserName = getAuthUserDisplayName(currentUser);
  const actorId = event.performedBy?.trim();
  const currentUserIds = [
    currentUser?.userId,
    currentUser?.username,
    currentUser?.email,
  ].filter(Boolean);
  const matchesCurrentUser = currentUserIds.some((id) => id === actorId);
  const userDrivenEvents = new Set([
    "CREATED",
    "UPDATED",
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
    "PUBLISHED",
    "RETIRED",
    "VERSION_CREATED",
  ]);

  if (
    currentUserName &&
    (matchesCurrentUser ||
      (!actorName && userDrivenEvents.has(event.eventType)) ||
      (actorName?.toLowerCase() === "system" &&
        userDrivenEvents.has(event.eventType)))
  ) {
    return currentUserName;
  }

  return actorName || "System";
}

/* ── Document-type badge colours ── */
const docTypeBadgeStyles: Record<string, string> = {
  POLICY:
    "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:ring-1 dark:ring-blue-500/30",
  STANDARD:
    "bg-slate-100 text-slate-700 ring-1 ring-slate-300 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30",
  GUIDELINE:
    "bg-teal-50 text-teal-700 ring-1 ring-teal-200 dark:bg-teal-500/20 dark:text-teal-300 dark:ring-1 dark:ring-teal-500/30",
  PROCEDURE:
    "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30",
};
const defaultDocTypeBadge =
  "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-500/20 dark:text-slate-300 dark:ring-1 dark:ring-slate-500/30";

/* ── Table columns for governance documents ── */
const getGovDocColumns = (
  page: number,
  pageSize: number,
): Column<GovDocRow>[] => [
    {
      key: "sno",
      header: "#",
      align: "center",
      render: (_row, idx) => (
        <span className="text-xs font-medium d">
          {(page - 1) * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "title",
      header: "Title",
      sortable: true,
      align: "left",
      headerAlign: "center",
      render: (row) => (
        <span className="block font-semibold break-words whitespace-normal">
          {row.title}
        </span>
      ),
    },
    {
      key: "documentType",
      header: "Type",
      sortable: true,
      align: "center",
      render: (row) => (
        <span
          className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold min-w-[5.5rem] ${DOC_TYPE_COLORS[row.documentType] ?? "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60"}`}
        >
          {formatDocType(row.documentType)}
        </span>
      ),
    },
    {
      key: "currentVersion",
      header: "Version",
      sortable: true,
      align: "center",
      render: (row) => (
        <span className="font-mono text-xs">v{row.currentVersion}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => (
        <StatusBadge
          status={
            mapLifecycleToVariant(row.status) as Parameters<
              typeof StatusBadge
            >[0]["status"]
          }
          dot={false}
        />
      ),
    },
    {
      key: "createdAt",
      header: "Created On",
      sortable: true,
      align: "center",
      render: (row) => <>{row.createdAt}</>,
    },
    {
      key: "updatedAt",
      header: "Updated On",
      sortable: true,
      align: "center",
      render: (row) => <>{row.updatedAt}</>,
    },
  ];

/* ── Table columns for evidence ── */
const getEvidenceColumns = (
  page: number,
  pageSize: number,
): Column<EvidenceRow>[] => [
    {
      key: "sno",
      header: "#",
      align: "center",
      render: (_row, idx) => (
        <span className="text-xs font-medium d">
          {(page - 1) * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "title",
      header: "Title",
      sortable: true,
      align: "left",
      headerAlign: "center",
      // Let the title use the full column width and wrap onto multiple lines
      // instead of truncating, so long titles stay fully readable.
      render: (row) => (
        <span className="block font-semibold break-words whitespace-normal">
          {row.title as string}
        </span>
      ),
    },
    {
      key: "tags",
      header: "Tags",
      align: "left",
      headerAlign: "center",
      render: (row) => {
        const tags = row.tags as string[];
        if (!tags.length) return <span className="text-xs d">-</span>;
        return (
          <div className="flex flex-wrap gap-1 py-1 max-w-[300px]">
            {tags.map((tag) => (
              <Tip
                key={tag}
                label={tag}
                side="top"
                className="inline-flex max-w-[90px]"
              >
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 w-full truncate">
                  <Tag className="size-2.5 flex-shrink-0" />
                  <span className="truncate">{tag}</span>
                </span>
              </Tip>
            ))}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created On",
      sortable: true,
      align: "center",
      render: (row) => <>{row.createdAt}</>,
    },
    {
      key: "updatedAt",
      header: "Updated On",
      sortable: true,
      align: "center",
      render: (row) => <>{row.updatedAt}</>,
    },
  ];

export function ContentRegistry() {
  const navigate = useNavigate();
  const { id: docIdFromUrl } = useParams<{ id: string }>();
  const currentUser = useAuthStore((state) => state.user);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState<"governance" | "evidence">(
    "governance",
  );

  /* ── Governance Documents state ── */
  const [documents, setDocuments] = useState<GovDocRow[]>([]);
  const [govLoading, setGovLoading] = useState(true);
  const [govError, setGovError] = useState<string | null>(null);
  const [govSearch, setGovSearch] = useState("");
  const [govPage, setGovPage] = useState(1);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96]. viewMode is shared
  // across the Governance Docs and Evidence sections below, so both of their
  // page sizes need the same card-valid default.
  const [govPageSize, setGovPageSize] = useState(getPageSizeOptions("card")[0]);
  const [filterType, setFilterType] = useState<GovernanceDocumentType | "">("");
  const [filterStatus, setFilterStatus] = useState<
    DocumentLifecycleStatus | ""
  >("");
  const [selectedGovDoc, setSelectedGovDoc] =
    useState<GovernanceDocumentResponse | null>(null);
  const [govDetailLoading, setGovDetailLoading] = useState(false);
  const [docHistory, setDocHistory] = useState<HistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* ── Evidence state ── */
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(true);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const [evPage, setEvPage] = useState(1);
  // Same reasoning as govPageSize above — must start valid for the shared
  // "card" default viewMode.
  const [evPageSize, setEvPageSize] = useState(getPageSizeOptions("card")[0]);
  const [selectedEvidence, setSelectedEvidence] =
    useState<SupportingEvidenceResponse | null>(null);
  const [evidenceDetailLoading, setEvidenceDetailLoading] = useState(false);

  // viewMode is shared by both the Governance Docs and Evidence sections
  // (single toggle state, two independent page sizes) — so switching it from
  // either section must re-validate BOTH page sizes against the new view's
  // option list, not just the one the toggle happens to sit next to.
  const handleViewModeChange = (mode: "card" | "table") => {
    setViewMode(mode);
    setGovPageSize((prev) => resolvePageSizeForViewMode(prev, mode));
    setEvPageSize((prev) => resolvePageSizeForViewMode(prev, mode));
    setGovPage(1);
    setEvPage(1);
  };

  /* ── Create Governance Doc modal state ── */
  const [showCreateGov, setShowCreateGov] = useState(false);
  const [createGovStep, setCreateGovStep] = useState<"details" | "upload">(
    "details",
  );
  const [createdGovDocId, setCreatedGovDocId] = useState<string | null>(null);
  const [newGovTitle, setNewGovTitle] = useState("");
  const [newGovDescription, setNewGovDescription] = useState("");
  const [newGovType, setNewGovType] = useState<GovernanceDocumentType | "">("");
  const [newGovReviewCycle, setNewGovReviewCycle] = useState<
    "" | "none" | "90" | "180" | "365" | "custom"
  >("");
  const [newGovCustomDays, setNewGovCustomDays] = useState("");
  const [createGovFile, setCreateGovFile] = useState<File | null>(null);
  /* Step 2 substate: PICKING → CREATING → VALIDATING → RESULTS → UPLOADING (or ERROR) */
  const [createGovStep2, setCreateGovStep2] = useState<
    "PICKING" | "CREATING" | "VALIDATING" | "RESULTS" | "UPLOADING" | "ERROR"
  >("PICKING");
  const [createGovValidationResult, setCreateGovValidationResult] =
    useState<ValidateFileResult | null>(null);
  const [createGovValidationError, setCreateGovValidationError] = useState<{
    title: string;
    message: string;
    code?: string;
  } | null>(null);
  const createGovValidateAbortRef = React.useRef<AbortController | null>(null);
  /**
   * Tracks the created-but-not-yet-successfully-uploaded DRAFT so we can
   * fire a keepalive DELETE on user dismissal / tab close / unmount.
   * Cleared the moment the doc is no longer orphaned (success or
   * server-side discard).
   */
  const orphanGovDocIdRef = React.useRef<string | null>(null);
  const createGovFileInputRef = React.useRef<HTMLInputElement>(null);

  // Accessible orgs for create
  const [accessibleOrgs, setAccessibleOrgs] = useState<
    { id: string; name: string; shortName: string }[]
  >([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [newGovOrgId, setNewGovOrgId] = useState("");

  /* ── Create Evidence modal state ── */
  const [showCreateEvidence, setShowCreateEvidence] = useState(false);
  const [createEvidenceStep, setCreateEvidenceStep] = useState<
    "details" | "upload"
  >("details");
  const [createdEvidenceId, setCreatedEvidenceId] = useState<string | null>(
    null,
  );
  const [creatingEvidence, setCreatingEvidence] = useState(false);
  const [newEvidenceTitle, setNewEvidenceTitle] = useState("");
  const [newEvidenceDescription, setNewEvidenceDescription] = useState("");
  const [newEvidenceTags, setNewEvidenceTags] = useState("");
  const [createEvidenceFile, setCreateEvidenceFile] = useState<File | null>(
    null,
  );
  const [uploadingCreateEvidenceFile, setUploadingCreateEvidenceFile] =
    useState(false);
  const createEvidenceFileInputRef = React.useRef<HTMLInputElement>(null);
  const [newEvidenceOrgId, setNewEvidenceOrgId] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  /* ── Edit Governance Doc state ── */
  const pendingEditRef = React.useRef(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editReviewCycle, setEditReviewCycle] = useState<
    "none" | "90" | "180" | "365" | "custom"
  >("none");
  const [editCustomDays, setEditCustomDays] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  /* ── Edit Evidence state ── */
  const [editEvidenceMode, setEditEvidenceMode] = useState(false);
  const [editEvidenceTitle, setEditEvidenceTitle] = useState("");
  const [editEvidenceDescription, setEditEvidenceDescription] = useState("");
  const [editEvidenceTags, setEditEvidenceTags] = useState("");
  const [savingEvidenceEdit, setSavingEvidenceEdit] = useState(false);

  /* ── Edit Evidence Modal (from table) ── */
  const [showEditEvidenceModal, setShowEditEvidenceModal] = useState(false);
  const [editingEvidenceId, setEditingEvidenceId] = useState<string | null>(
    null,
  );

  /* ── Preview Evidence Modal (from table) ── */
  const [previewEvidenceRow, setPreviewEvidenceRow] =
    useState<EvidenceRow | null>(null);

  /* ── Gov docs sort state ── */
  const [docSortKey, setDocSortKey] = useState<string | null>(null);
  const [docSortDir, setDocSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default original order).
  const handleDocSort = (key: string) => {
    if (docSortKey !== key) {
      setDocSortKey(key);
      setDocSortDir("asc");
    } else if (docSortDir === "asc") {
      setDocSortDir("desc");
    } else {
      setDocSortKey(null);
      setDocSortDir("asc");
    }
  };

  /* ── Evidence sort state ── */
  const [evSortKey, setEvSortKey] = useState<string | null>(null);
  const [evSortDir, setEvSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default original order).
  const handleEvSort = (key: string) => {
    if (evSortKey !== key) {
      setEvSortKey(key);
      setEvSortDir("asc");
    } else if (evSortDir === "asc") {
      setEvSortDir("desc");
    } else {
      setEvSortKey(null);
      setEvSortDir("asc");
    }
  };

  /* ── Table preview state ── */
  const [tablePreviewDocId, setTablePreviewDocId] = useState<string | null>(
    null,
  );
  // Cache-bust token captured each time a preview opens. Replacing a file keeps
  // the same doc id (and often the same version), so without a fresh token the
  // browser would serve the stale cached PDF. A per-open timestamp guarantees
  // the current file is fetched.
  const [tablePreviewToken, setTablePreviewToken] = useState(0);
  const openTablePreview = useCallback((id: string) => {
    setTablePreviewDocId(id);
    setTablePreviewToken(Date.now());
  }, []);
  const closeTablePreview = useCallback(() => setTablePreviewDocId(null), []);

  /* ── Delete state ── */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetType, setDeleteTargetType] = useState<
    "governance" | "evidence"
  >("governance");

  /* ── Lifecycle modal state ── */
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState<string>("");
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [lifecycleProcessing, setLifecycleProcessing] = useState(false);

  /* ── Upload modal state ── */
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTargetType, setUploadTargetType] = useState<
    "governance" | "evidence"
  >("governance");

  /* ── Fetch governance documents ── */
  const fetchGovDocuments = useCallback(async () => {
    setGovLoading(true);
    setGovError(null);
    try {
      const res = await listGovernanceDocuments({
        type: filterType || undefined,
        status: filterStatus || undefined,
      });
      const rows = (res.data ?? []).map(toGovDocRow);
      setDocuments(rows);
    } catch (err: unknown) {
      setGovError(
        err instanceof Error ? err.message : "Failed to load documents",
      );
    } finally {
      setGovLoading(false);
    }
  }, [filterType, filterStatus]);

  /* ── Fetch evidence ── */
  const fetchEvidence = useCallback(async () => {
    setEvidenceLoading(true);
    setEvidenceError(null);
    try {
      const res = await listSupportingEvidence();
      const rows = (res.data ?? []).map(toEvidenceRow);
      setEvidence(rows);
    } catch (err: unknown) {
      setEvidenceError(
        err instanceof Error ? err.message : "Failed to load evidence",
      );
    } finally {
      setEvidenceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "governance") {
      fetchGovDocuments();
    } else {
      fetchEvidence();
    }
  }, [activeTab, fetchGovDocuments, fetchEvidence]);

  /* ── Fetch governance document detail ── */
  const fetchGovDocDetail = useCallback(async (id: string) => {
    setGovDetailLoading(true);
    setHistoryLoading(true);
    try {
      const [docRes, historyRes] = await Promise.all([
        getGovernanceDocument(id),
        getGovernanceDocumentHistory(id),
      ]);
      setSelectedGovDoc(docRes.data);
      setDocHistory(historyRes.data ?? []);
    } catch {
      showToast("error", "Failed to load document details");
      setSelectedGovDoc(null);
      setDocHistory([]);
    } finally {
      setGovDetailLoading(false);
      setHistoryLoading(false);
    }
  }, []);

  /* ── Fetch evidence detail ── */
  const fetchEvidenceDetail = useCallback(async (id: string) => {
    setEvidenceDetailLoading(true);
    try {
      const res = await getSupportingEvidence(id);
      setSelectedEvidence(res.data);
    } catch {
      showToast("error", "Failed to load evidence details");
      setSelectedEvidence(null);
    } finally {
      setEvidenceDetailLoading(false);
    }
  }, []);

  /* ── Handlers ── */
  const handleSelectGovDoc = (row: GovDocRow) => {
    navigate(`/content-registry/${row.id}`);
    fetchGovDocDetail(row.id);
  };

  const handleSelectEvidence = (row: EvidenceRow) => {
    navigate(`/content-registry/${row.id}`);
    fetchEvidenceDetail(row.id);
  };

  const handleBack = () => {
    navigate("/content-registry");
    setSelectedGovDoc(null);
    setSelectedEvidence(null);
    setEditMode(false);
    setEditEvidenceMode(false);
  };

  /* ── Load document from URL parameter on mount ── */
  useEffect(() => {
    if (!docIdFromUrl) return;

    let cancelled = false;

    const loadDocFromUrl = async () => {
      if (activeTab === "governance") {
        // For governance docs
        const existingDoc = documents.find((d) => d.id === docIdFromUrl);
        if (existingDoc && !cancelled) {
          fetchGovDocDetail(docIdFromUrl);
        } else if (!cancelled) {
          // Directly fetch if not in list yet
          fetchGovDocDetail(docIdFromUrl);
        }
      } else {
        // For evidence
        const existingEv = evidence.find((e) => e.id === docIdFromUrl);
        if (existingEv && !cancelled) {
          fetchEvidenceDetail(docIdFromUrl);
        } else if (!cancelled) {
          // Directly fetch if not in list yet
          fetchEvidenceDetail(docIdFromUrl);
        }
      }
    };

    loadDocFromUrl();
    return () => {
      cancelled = true;
    };
  }, [
    docIdFromUrl,
    documents,
    evidence,
    activeTab,
    fetchGovDocDetail,
    fetchEvidenceDetail,
  ]);

  /* ── Create Governance Doc handlers ── */
  const handleCreateGovStep1 = () => {
    if (!newGovTitle.trim()) return;
    if (!newGovType) return;
    if (!newGovReviewCycle) return;
    if (
      newGovReviewCycle === "custom" &&
      (!newGovCustomDays || parseInt(newGovCustomDays, 10) <= 0)
    ) {
      showToast("error", "Please enter a valid number of days");
      return;
    }
    setCreateGovStep("upload");
  };

  /** Step 2 — Validate the picked file's size before accepting it. */
  const handleCreateGovFileSelect = (file: File | null) => {
    if (file && file.size > MAX_FILE_BYTES) {
      showToast(
        "error",
        `File is too large (${formatFileSize(file.size)}). Maximum allowed size is 25 MB.`,
      );
      setCreateGovFile(null);
      if (createGovFileInputRef.current) {
        createGovFileInputRef.current.value = "";
      }
      return;
    }
    setCreateGovFile(file);
  };

  /**
   * Step 2 — Validate the picked file against the org's active rules.
   * Creates the DRAFT doc on the first call (so we have an id to validate
   * against), then runs /validate-file. Reuses the same docId on subsequent
   * "Pick Different File" → re-validate cycles.
   */
  const handleCreateGovValidate = async () => {
    if (!createGovFile) return;

    setCreateGovValidationError(null);
    setCreateGovValidationResult(null);

    let docId = createdGovDocId;

    // (a) Create the doc on first attempt
    if (!docId) {
      const requiresReview =
        newGovReviewCycle !== "none" && newGovReviewCycle !== "";
      const reviewCycleDays =
        newGovReviewCycle === "custom"
          ? parseInt(newGovCustomDays, 10)
          : parseInt(newGovReviewCycle, 10);
      setCreateGovStep2("CREATING");
      try {
        const res = await createGovernanceDocument({
          documentType: newGovType as GovernanceDocumentType,
          title: newGovTitle.trim(),
          description: newGovDescription.trim() || undefined,
          owningOrgUnitId: newGovOrgId || undefined,
          requiresReview,
          reviewCycleDays: requiresReview ? reviewCycleDays : undefined,
          reviewAlertDays: requiresReview ? 15 : undefined,
        });
        docId = res.data?.id ?? null;
        if (!docId) throw new Error("Document creation returned no id.");
        setCreatedGovDocId(docId);
        orphanGovDocIdRef.current = docId;
      } catch (err: unknown) {
        setCreateGovValidationError({
          title: "Couldn't create document",
          message:
            err instanceof Error ? err.message : "Document creation failed.",
        });
        setCreateGovStep2("ERROR");
        return;
      }
    }

    // (b) Validate the file
    createGovValidateAbortRef.current?.abort();
    const ctrl = new AbortController();
    createGovValidateAbortRef.current = ctrl;

    setCreateGovStep2("VALIDATING");
    try {
      const result = await validateGovernanceDocumentFile(
        docId,
        createGovFile,
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      setCreateGovValidationResult(result);
      setCreateGovStep2("RESULTS");
    } catch (err: unknown) {
      if (
        (err instanceof DOMException && err.name === "AbortError") ||
        (err instanceof Error && err.name === "AbortError")
      ) {
        return;
      }
      if (err instanceof ApiEnvelopeError) {
        // LLM-0005-E — owning org has no active LLM. The BE detail names the org,
        // so surface a clear instruction and pass the BE message through as context.
        const isNoActiveLlm = err.code === "LLM-0005-E";
        setCreateGovValidationError({
          title: isNoActiveLlm
            ? "No active LLM for this document's organization"
            : err.title || "Validation failed",
          message: isNoActiveLlm
            ? `${err.detail} Configure an active LLM under LLM Configurations for this organization to enable validation.`
            : err.detail,
          code: err.code,
        });
      } else {
        setCreateGovValidationError({
          title: "Validation failed",
          message: err instanceof Error ? err.message : "Something went wrong.",
        });
      }
      setCreateGovStep2("ERROR");
    } finally {
      if (createGovValidateAbortRef.current === ctrl) {
        createGovValidateAbortRef.current = null;
      }
    }
  };

  /** Step 2 — User clicked "Upload" on the results panel. */
  const handleCreateGovFinalUpload = async () => {
    if (!createGovFile || !createdGovDocId) return;
    setCreateGovStep2("UPLOADING");
    try {
      await uploadGovernanceDocumentFile(createdGovDocId, createGovFile);
      // Doc is real now — clear the orphan tracker before any close path
      // runs, so a tab-close in the success path doesn't fire a stray DELETE.
      orphanGovDocIdRef.current = null;
      showToast("success", "Document created and file uploaded successfully");
      setShowCreateGov(false);
      resetCreateGovForm();
      fetchGovDocuments();
    } catch (err: unknown) {
      // CR-0303-E: backend's server-side validation rejected the upload.
      // First-upload in the create flow → backend soft-deletes the draft.
      if (err instanceof ApiEnvelopeError && err.code === "CR-0303-E") {
        // Server already deleted (discarded) — or kept it (retained, unlikely
        // in the create flow). Either way, don't re-fire DELETE on cleanup.
        orphanGovDocIdRef.current = null;
        const discarded = err.detail.includes("discarded");
        showToast(
          "error",
          discarded
            ? "Draft discarded. The file did not match the document's metadata or org rules."
            : "Upload rejected by server-side validation.",
        );
        setShowCreateGov(false);
        resetCreateGovForm();
        fetchGovDocuments();
        return;
      }
      if (err instanceof ApiEnvelopeError && err.code === "LLM-0005-E") {
        setCreateGovValidationError({
          title: "No active LLM for this document's organization",
          message: `${err.detail} An administrator must activate an LLM for this organization under LLM Configurations before validation can run.`,
          code: err.code,
        });
        setCreateGovStep2("ERROR");
        return;
      }
      setCreateGovValidationError({
        title: "Upload failed",
        message: err instanceof Error ? err.message : "Failed to upload file.",
      });
      setCreateGovStep2("ERROR");
    }
  };

  /** Step 2 — Reset to file picker, but keep the created doc so we can re-validate. */
  const handleCreateGovPickDifferent = () => {
    createGovValidateAbortRef.current?.abort();
    createGovValidateAbortRef.current = null;
    setCreateGovFile(null);
    if (createGovFileInputRef.current) {
      createGovFileInputRef.current.value = "";
    }
    setCreateGovValidationResult(null);
    setCreateGovValidationError(null);
    setCreateGovStep2("PICKING");
  };

  /** Step 2 — Cancel a VALIDATING in-flight request and reset to PICKING. */
  const handleCreateGovCancelValidating = () => {
    createGovValidateAbortRef.current?.abort();
    createGovValidateAbortRef.current = null;
    setCreateGovStep2("PICKING");
  };

  /** Step 2 — Footer per substate. */
  const renderCreateGovStep2Footer = () => {
    const closeAll = dismissCreateGovWizard;
    switch (createGovStep2) {
      case "PICKING":
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={closeAll}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateGovValidate}
              disabled={!createGovFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40"
            >
              Validate &amp; Continue
            </button>
          </div>
        );
      case "CREATING":
        return (
          <div className="flex w-full justify-end">
            <button
              disabled
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 opacity-40 cursor-not-allowed"
            >
              Creating document…
            </button>
          </div>
        );
      case "VALIDATING":
        return (
          <div className="flex w-full justify-end">
            <button
              onClick={handleCreateGovCancelValidating}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors flex items-center gap-2"
            >
              <X className="size-3.5" />
              Cancel
            </button>
          </div>
        );
      case "UPLOADING":
        return (
          <div className="flex w-full justify-end">
            <button
              disabled
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 opacity-40 cursor-not-allowed"
            >
              Uploading…
            </button>
          </div>
        );
      case "RESULTS": {
        const canUpload = createGovValidationResult
          ? isValidationPassed(createGovValidationResult)
          : false;
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={closeAll}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateGovFinalUpload}
              disabled={!canUpload}
              title={
                canUpload
                  ? undefined
                  : "Validation failed — pick a different file to enable upload."
              }
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
            >
              <Upload className="size-3.5" />
              Save
            </button>
          </div>
        );
      }
      case "ERROR":
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={closeAll}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateGovPickDifferent}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Choose Different File
            </button>
            <button
              onClick={handleCreateGovValidate}
              disabled={!createGovFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40"
            >
              Retry
            </button>
          </div>
        );
    }
  };

  const handleSkipGovUpload = () => {
    setShowCreateGov(false);
    resetCreateGovForm();
  };

  const resetCreateGovForm = () => {
    createGovValidateAbortRef.current?.abort();
    createGovValidateAbortRef.current = null;
    orphanGovDocIdRef.current = null;
    setNewGovTitle("");
    setNewGovDescription("");
    setNewGovType("");
    setNewGovReviewCycle("");
    setNewGovCustomDays("");
    setCreateGovFile(null);
    setCreateGovStep("details");
    setCreatedGovDocId(null);
    setNewGovOrgId("");
    setCreateGovStep2("PICKING");
    setCreateGovValidationResult(null);
    setCreateGovValidationError(null);
  };

  /**
   * Close the create wizard as an explicit user dismissal (Cancel / X / etc).
   * If a DRAFT was created in this session but never successfully uploaded,
   * fire a keepalive DELETE so we don't leave an orphan in the DB.
   */
  const dismissCreateGovWizard = () => {
    const orphanId = orphanGovDocIdRef.current;
    if (orphanId) {
      deleteGovernanceDocumentOrphan(orphanId);
    }
    setShowCreateGov(false);
    resetCreateGovForm();
  };

  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setNewGovOrgId(orgs[0].id);
        setNewEvidenceOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  /* ── Create Evidence handlers ── */
  const handleCreateEvidenceStep1 = () => {
    if (!newEvidenceTitle.trim() || getTitleValidationError(newEvidenceTitle))
      return;
    setCreateEvidenceStep("upload");
  };

  const handleCreateEvidenceUpload = async () => {
    if (!createEvidenceFile) return;
    setUploadingCreateEvidenceFile(true);
    try {
      const tags = newEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const res = await createSupportingEvidence({
        title: newEvidenceTitle.trim(),
        description: newEvidenceDescription.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        owningOrgUnitId: newEvidenceOrgId || undefined,
      });
      const evidenceId = res.data?.id;
      if (evidenceId) {
        await uploadSupportingEvidenceFile(evidenceId, createEvidenceFile);
        // The upload endpoint overwrites the evidence title with the uploaded
        // file name, so re-assert the user-entered title afterwards.
        await updateSupportingEvidence(evidenceId, {
          title: newEvidenceTitle.trim(),
        });
      }
      showToast("success", "Evidence created and file uploaded successfully");
      setShowCreateEvidence(false);
      resetCreateEvidenceForm();
      fetchEvidence();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to create evidence",
      );
    } finally {
      setUploadingCreateEvidenceFile(false);
    }
  };

  const handleSkipEvidenceUpload = () => {
    setShowCreateEvidence(false);
    resetCreateEvidenceForm();
  };

  const resetCreateEvidenceForm = () => {
    setNewEvidenceTitle("");
    setNewEvidenceDescription("");
    setNewEvidenceTags("");
    setCreateEvidenceFile(null);
    setCreateEvidenceStep("details");
    setCreatedEvidenceId(null);
    setNewEvidenceOrgId("");
  };

  /* ── Tag autocomplete ── */
  const handleTagInputChange = async (value: string) => {
    setNewEvidenceTags(value);
    const lastTag = value.split(",").pop()?.trim();
    if (lastTag && lastTag.length >= 2) {
      try {
        const res = await searchEvidenceTags(lastTag);
        setTagSuggestions(res.data ?? []);
      } catch {
        setTagSuggestions([]);
      }
    } else {
      setTagSuggestions([]);
    }
  };

  /**
   * Orphan-DRAFT cleanup. If the user creates a DRAFT in the wizard,
   * never completes the upload, and then navigates away or closes the
   * tab, fire a best-effort DELETE so we don't leak rows.
   *
   * The ref is the source of truth; explicit dismiss paths already fire
   * DELETE via `dismissCreateGovWizard` and null the ref, so these
   * listeners are a safety net for the implicit-exit cases.
   */
  useEffect(() => {
    const flushOrphan = () => {
      const id = orphanGovDocIdRef.current;
      if (!id) return;
      orphanGovDocIdRef.current = null;
      deleteGovernanceDocumentOrphan(id);
    };
    window.addEventListener("beforeunload", flushOrphan);
    return () => {
      window.removeEventListener("beforeunload", flushOrphan);
      // Also fire on unmount (user navigates away to a different route)
      flushOrphan();
    };
  }, []);

  /* ── Auto-enter edit mode when navigating from table edit action ── */
  useEffect(() => {
    if (pendingEditRef.current && selectedGovDoc && !govDetailLoading) {
      pendingEditRef.current = false;
      setEditTitle(selectedGovDoc.title);
      setEditDescription(selectedGovDoc.description ?? "");
      if (!selectedGovDoc.requiresReview) {
        setEditReviewCycle("none");
        setEditCustomDays("");
      } else {
        const days = selectedGovDoc.reviewCycleDays;
        if (days === 90) setEditReviewCycle("90");
        else if (days === 180) setEditReviewCycle("180");
        else if (days === 365) setEditReviewCycle("365");
        else {
          setEditReviewCycle("custom");
          setEditCustomDays(String(days ?? ""));
        }
      }
      setEditMode(true);
    }
  }, [selectedGovDoc, govDetailLoading]);

  /* ── Edit Governance Doc handlers ── */
  const enterEditMode = () => {
    if (!selectedGovDoc) return;
    setEditTitle(selectedGovDoc.title);
    setEditDescription(selectedGovDoc.description ?? "");
    // Convert reviewCycleDays to dropdown format
    if (!selectedGovDoc.requiresReview) {
      setEditReviewCycle("none");
      setEditCustomDays("");
    } else {
      const days = selectedGovDoc.reviewCycleDays;
      if (days === 90) setEditReviewCycle("90");
      else if (days === 180) setEditReviewCycle("180");
      else if (days === 365) setEditReviewCycle("365");
      else {
        setEditReviewCycle("custom");
        setEditCustomDays(String(days ?? ""));
      }
    }
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedGovDoc) return;
    if (
      editReviewCycle === "custom" &&
      (!editCustomDays || parseInt(editCustomDays, 10) <= 0)
    ) {
      showToast("error", "Please enter a valid number of days");
      return;
    }
    const requiresReview = editReviewCycle !== "none";
    const reviewCycleDays =
      editReviewCycle === "custom"
        ? parseInt(editCustomDays, 10)
        : parseInt(editReviewCycle, 10);
    setSavingEdit(true);
    try {
      await updateGovernanceDocument(selectedGovDoc.id, {
        title: editTitle.trim() || undefined,
        description: editDescription.trim() || undefined,
        requiresReview,
        reviewCycleDays: requiresReview ? reviewCycleDays : undefined,
      });
      showToast("success", "Document updated");
      setEditMode(false);
      fetchGovDocDetail(selectedGovDoc.id);
      fetchGovDocuments();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update document",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  /* ── Edit Evidence handlers ── */
  const enterEditEvidenceMode = () => {
    if (!selectedEvidence) return;
    setEditEvidenceTitle(selectedEvidence.title);
    setEditEvidenceDescription(selectedEvidence.description ?? "");
    setEditEvidenceTags(selectedEvidence.tags.join(", "));
    setEditEvidenceMode(true);
  };

  const handleSaveEvidenceEdit = async () => {
    if (!selectedEvidence) return;
    setSavingEvidenceEdit(true);
    try {
      const tags = editEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await updateSupportingEvidence(selectedEvidence.id, {
        title: editEvidenceTitle.trim() || undefined,
        description: editEvidenceDescription.trim() || undefined,
        tags,
      });
      showToast("success", "Evidence updated");
      setEditEvidenceMode(false);
      fetchEvidenceDetail(selectedEvidence.id);
      fetchEvidence();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update evidence",
      );
    } finally {
      setSavingEvidenceEdit(false);
    }
  };

  /* ── Edit Evidence from table ── */
  const openEditEvidenceModal = (row: EvidenceRow) => {
    setEditingEvidenceId(row.id);
    setEditEvidenceTitle(row.title);
    setEditEvidenceDescription(row.description);
    setEditEvidenceTags(row.tags.join(", "));
    setShowEditEvidenceModal(true);
  };

  const handleSaveEvidenceFromModal = async () => {
    if (!editingEvidenceId) return;
    setSavingEvidenceEdit(true);
    try {
      const tags = editEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await updateSupportingEvidence(editingEvidenceId, {
        title: editEvidenceTitle.trim() || undefined,
        description: editEvidenceDescription.trim() || undefined,
        tags,
      });
      showToast("success", "Evidence updated");
      setShowEditEvidenceModal(false);
      setEditingEvidenceId(null);
      fetchEvidence();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update evidence",
      );
    } finally {
      setSavingEvidenceEdit(false);
    }
  };

  /* ── Delete handlers ── */
  const handleDeleteGovDoc = (id: string) => {
    setDeleteTargetId(id);
    setDeleteTargetType("governance");
    setDeleteModalOpen(true);
  };

  const handleDeleteEvidence = (id: string) => {
    setDeleteTargetId(id);
    setDeleteTargetType("evidence");
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteModalOpen(false);
    setDeleting(deleteTargetId);
    try {
      if (deleteTargetType === "governance") {
        await deleteGovernanceDocument(deleteTargetId);
        showToast("success", "Document deleted");
        fetchGovDocuments();
        if (selectedGovDoc?.id === deleteTargetId) {
          navigate("/content-registry");
          setSelectedGovDoc(null);
        }
      } else {
        await deleteSupportingEvidence(deleteTargetId);
        showToast("success", "Evidence deleted");
        fetchEvidence();
        if (selectedEvidence?.id === deleteTargetId) {
          navigate("/content-registry");
          setSelectedEvidence(null);
        }
      }
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete",
      );
    } finally {
      setDeleting(null);
      setDeleteTargetId(null);
    }
  };

  /* ── Lifecycle actions ── */
  const openLifecycleModal = (action: string) => {
    setLifecycleAction(action);
    setLifecycleReason("");
    setShowLifecycleModal(true);
  };

  const handleLifecycleAction = async () => {
    if (!selectedGovDoc) return;
    setLifecycleProcessing(true);
    try {
      switch (lifecycleAction) {
        case "submit":
          await submitGovernanceDocumentForReview(selectedGovDoc.id);
          showToast("success", "Submitted for review");
          break;
        case "approve":
          await reviewGovernanceDocument(selectedGovDoc.id, "APPROVE");
          showToast("success", "Document approved");
          break;
        case "publish":
          await reviewGovernanceDocument(selectedGovDoc.id, "PUBLISH");
          showToast("success", "Document published");
          break;
        case "reject":
          if (!lifecycleReason.trim()) {
            showToast("error", "Rejection reason is required");
            setLifecycleProcessing(false);
            return;
          }
          await reviewGovernanceDocument(
            selectedGovDoc.id,
            "REJECT",
            lifecycleReason,
          );
          showToast("success", "Document rejected");
          break;
        case "new-version":
          await createNewGovernanceDocumentVersion(selectedGovDoc.id);
          showToast("success", "New version created");
          break;
        case "retire":
          if (!lifecycleReason.trim()) {
            showToast("error", "Retirement reason is required");
            setLifecycleProcessing(false);
            return;
          }
          await retireGovernanceDocument(selectedGovDoc.id, lifecycleReason);
          showToast("success", "Document withdrawn");
          break;
      }
      setShowLifecycleModal(false);
      fetchGovDocDetail(selectedGovDoc.id);
      fetchGovDocuments();
    } catch (err: unknown) {
      showToast("error", err instanceof Error ? err.message : "Action failed");
    } finally {
      setLifecycleProcessing(false);
    }
  };

  /* ── Upload handlers ── */
  const openUploadModal = (type: "governance" | "evidence") => {
    setUploadTargetType(type);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleUploadFile = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      if (uploadTargetType === "governance" && selectedGovDoc) {
        await uploadGovernanceDocumentFile(selectedGovDoc.id, uploadFile);
        showToast("success", "File uploaded successfully");
        fetchGovDocDetail(selectedGovDoc.id);
      } else if (uploadTargetType === "evidence" && selectedEvidence) {
        await uploadSupportingEvidenceFile(selectedEvidence.id, uploadFile);
        showToast("success", "File uploaded successfully");
        fetchEvidenceDetail(selectedEvidence.id);
      }
      setShowUploadModal(false);
      setUploadFile(null);
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to upload file",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!selectedGovDoc) return;
    try {
      await deleteGovernanceDocumentFile(selectedGovDoc.id);
      showToast("success", "File removed successfully");
      fetchGovDocDetail(selectedGovDoc.id);
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete file",
      );
    }
  };

  /* ── Sort icon components ── */
  const DocSortIcon = ({ col }: { col: string }) => {
    const active = docSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && docSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && docSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const EvSortIcon = ({ col }: { col: string }) => {
    const active = evSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && evSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && evSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  /* ── Derived state ── */
  const filteredDocs = govSearch
    ? documents.filter((d) =>
      d.title.toLowerCase().includes(govSearch.toLowerCase()),
    )
    : documents;
  const govOriginalIndexMap = new Map(filteredDocs.map((d, i) => [d.id, i]));
  const sortedDocs = docSortKey
    ? [...filteredDocs].sort((a, b) => {
      const av = a[docSortKey as keyof GovDocRow];
      const bv = b[docSortKey as keyof GovDocRow];
      const cmp = av === bv ? 0 : (av ?? "") < (bv ?? "") ? -1 : 1;
      return docSortDir === "asc" ? cmp : -cmp;
    })
    : filteredDocs;
  const paginatedDocs = sortedDocs.slice(
    (govPage - 1) * govPageSize,
    govPage * govPageSize,
  );

  const filteredEvidence = evidenceSearch
    ? evidence.filter(
      (e) =>
        e.title.toLowerCase().includes(evidenceSearch.toLowerCase()) ||
        e.tags.some((t) =>
          t.toLowerCase().includes(evidenceSearch.toLowerCase()),
        ),
    )
    : evidence;
  const evOriginalIndexMap = new Map(filteredEvidence.map((e, i) => [e.id, i]));
  const sortedEvidence = evSortKey
    ? [...filteredEvidence].sort((a, b) => {
      const av = a[evSortKey as keyof EvidenceRow];
      const bv = b[evSortKey as keyof EvidenceRow];
      const cmp = av === bv ? 0 : (av ?? "") < (bv ?? "") ? -1 : 1;
      return evSortDir === "asc" ? cmp : -cmp;
    })
    : filteredEvidence;
  const paginatedEvidence = sortedEvidence.slice(
    (evPage - 1) * evPageSize,
    evPage * evPageSize,
  );

  const isGovDocEditable = selectedGovDoc?.status === "DRAFT";

  /* ── Loading skeleton while fetching detail ── */
  if (
    (docIdFromUrl && !selectedGovDoc && activeTab === "governance") ||
    (docIdFromUrl && !selectedEvidence && activeTab === "evidence")
  ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <SkeletonCard lines={6} />
        <SkeletonCard lines={4} />
      </div>
    );
  }

  /* ── Main content with conditional rendering ── */
  const mainContent = selectedGovDoc ? (
    <GovernanceDocumentDetail
      doc={selectedGovDoc}
      loading={govDetailLoading}
      editMode={editMode}
      editTitle={editTitle}
      editDescription={editDescription}
      editReviewCycle={editReviewCycle}
      editCustomDays={editCustomDays}
      savingEdit={savingEdit}
      onBack={handleBack}
      onEdit={enterEditMode}
      onCancelEdit={() => setEditMode(false)}
      onSaveEdit={handleSaveEdit}
      onEditTitleChange={setEditTitle}
      onEditDescriptionChange={setEditDescription}
      onEditReviewCycleChange={setEditReviewCycle}
      onEditCustomDaysChange={setEditCustomDays}
      onLifecycleAction={openLifecycleModal}
      onUpload={() => openUploadModal("governance")}
      onRemoveFile={handleRemoveFile}
      onDelete={() => handleDeleteGovDoc(selectedGovDoc.id)}
      isEditable={isGovDocEditable}
      history={docHistory}
      historyLoading={historyLoading}
    />
  ) : selectedEvidence ? (
    <EvidenceDetail
      evidence={selectedEvidence}
      loading={evidenceDetailLoading}
      editMode={editEvidenceMode}
      editTitle={editEvidenceTitle}
      editDescription={editEvidenceDescription}
      editTags={editEvidenceTags}
      savingEdit={savingEvidenceEdit}
      onBack={handleBack}
      onEdit={enterEditEvidenceMode}
      onCancelEdit={() => setEditEvidenceMode(false)}
      onSaveEdit={handleSaveEvidenceEdit}
      onEditTitleChange={setEditEvidenceTitle}
      onEditDescriptionChange={setEditEvidenceDescription}
      onEditTagsChange={setEditEvidenceTags}
      onUpload={() => openUploadModal("evidence")}
      onDelete={() => handleDeleteEvidence(selectedEvidence.id)}
    />
  ) : (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Content Registry</h1>
          <p className="text-sm d mt-0.5">
            Manage governance documents and supporting evidence
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {activeTab === "governance" ? (
            <RBACAction module="document" action="create">
              <button
                onClick={() => {
                  resetCreateGovForm();
                  fetchAccessibleOrgs("document", "create");
                  setShowCreateGov(true);
                }}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Document
              </button>
            </RBACAction>
          ) : (
            <RBACAction module="evidence" action="create">
              <button
                onClick={() => {
                  resetCreateEvidenceForm();
                  fetchAccessibleOrgs("evidence", "create");
                  setShowCreateEvidence(true);
                }}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Evidence
              </button>
            </RBACAction>
          )}
        </div>
      </div>

      {/* Main tabs: Governance vs Evidence */}
      <div className="flex items-center gap-1 p-1 bg-muted/50 dark:bg-white/[0.03] rounded-lg w-fit">
        <button
          onClick={() => {
            setActiveTab("governance");
            setFilterType("");
            setFilterStatus("");
          }}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === "governance"
              ? "bg-background dark:bg-white/[0.08] text-foreground shadow-sm"
              : "d hover:text-foreground hover:bg-background/50 dark:hover:bg-white/[0.04]"
            }`}
        >
          <FolderOpen className="size-4" />
          Governance Documents
        </button>
        <button
          onClick={() => {
            setActiveTab("evidence");
            setFilterType("");
            setFilterStatus("");
          }}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === "evidence"
              ? "bg-background dark:bg-white/[0.08] text-foreground shadow-sm"
              : "d hover:text-foreground hover:bg-background/50 dark:hover:bg-white/[0.04]"
            }`}
        >
          <FilePlus className="size-4" />
          Supporting Evidence
        </button>
      </div>

      {activeTab === "governance" ? (
        <>
          {/* Error banner */}
          {govError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <span>{govError}</span>
              <button
                onClick={fetchGovDocuments}
                className="ml-auto text-xs underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search documents"
                value={govSearch}
                onChange={(e) => {
                  setGovSearch(e.target.value);
                  setGovPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm placeholder:d/50 outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all"
              />
            </div>
            <Dropdown
              size="sm"
              variant="pill"
              value={filterType}
              onChange={(v) => setFilterType(v as GovernanceDocumentType | "")}
              options={[
                { value: "", label: "All Types" },
                ...DOCUMENT_TYPES.map((t) => ({
                  value: t.value,
                  label: t.label,
                })),
              ]}
            />
            <Dropdown
              size="sm"
              variant="pill"
              value={filterStatus}
              onChange={(v) =>
                setFilterStatus(v as DocumentLifecycleStatus | "")
              }
              options={[
                { value: "", label: "All Status" },
                ...LIFECYCLE_STATUSES.map((s) => ({
                  value: s.value,
                  label: s.label,
                  dot:
                    s.value === "PUBLISHED"
                      ? "bg-emerald-500"
                      : s.value === "IN_REVIEW"
                        ? "bg-blue-500"
                        : s.value === "DRAFT"
                          ? "bg-slate-400"
                          : undefined,
                })),
              ]}
            />
            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
              <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
              <label className="text-sm d whitespace-nowrap">
                Rows per page
              </label>
              <Dropdown
                value={String(govPageSize)}
                onChange={(v) => {
                  setGovPageSize(Number(v));
                  setGovPage(1);
                }}
                options={getPageSizeOptions(viewMode).map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>

          {/* Table with built-in pagination */}
          <DataTable
            columns={
              [
                ...getGovDocColumns(govPage, govPageSize)
                  .filter((col) =>
                    filterType ? col.key !== "documentType" : true,
                  )
                  .map((col) => {
                    if (col.key === "sno") {
                      return {
                        ...col,
                        render: (row: GovDocRow) => (
                          <span className="text-xs font-medium d">
                            {(govOriginalIndexMap.get(row.id) ?? 0) + 1}
                          </span>
                        ),
                      };
                    }
                    const docSortableLabels: Record<string, string> = {
                      title: "Title",
                      documentType: "Type",
                      currentVersion: "Version",
                      status: "Status",
                      createdAt: "Created On",
                      updatedAt: "Updated On",
                    };
                    if (col.key in docSortableLabels) {
                      return {
                        ...col,
                        header: (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocSort(col.key);
                            }}
                            className="inline-flex items-center whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                          >
                            {docSortableLabels[col.key]}
                            <DocSortIcon col={col.key} />
                          </button>
                        ),
                      };
                    }
                    return col;
                  }),
                {
                  key: "actions",
                  header: "Action",
                  headerClassName: "text-center",
                  render: (row: GovDocRow) => (
                    <div className="flex items-center justify-center gap-1">
                      {row.hasFile ? (
                        <>
                          <Tip label="Preview">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openTablePreview(row.id);
                              }}
                              className="p-1.5 rounded-md hover:bg-accent d hover:text-foreground transition-all"
                            >
                              <Eye className="size-4" />
                            </button>
                          </Tip>
                          <Tip label="Download">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerDownload(
                                  downloadGovernanceDocumentFile(row.id),
                                  row.fileName as string | null | undefined,
                                );
                              }}
                              className="p-1.5 rounded-md hover:bg-accent d hover:text-foreground transition-all"
                            >
                              <Download className="size-4" />
                            </button>
                          </Tip>
                        </>
                      ) : (
                        <span className="text-xs d px-1">No file</span>
                      )}
                    </div>
                  ),
                },
              ] as Column<GovDocRow & Record<string, unknown>>[]
            }
            data={paginatedDocs}
            rowKey={(r) => r.id}
            onRowClick={handleSelectGovDoc}
            loading={govLoading}
            emptyText="No governance documents found"
            page={govPage - 1}
            pageSize={govPageSize}
            totalItems={sortedDocs.length}
            onPageChange={(p) => setGovPage(p + 1)}
            itemLabel="documents"
            enableCardView
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            renderCard={(rows) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
                {rows.map((row) => {
                  const variant = mapLifecycleToVariant(row.status) as Parameters<
                    typeof StatusBadge
                  >[0]["status"];
                  const accent = statusAccent(variant);
                  return (
                    <div
                      key={row.id}
                      onClick={() => handleSelectGovDoc(row)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectGovDoc(row);
                        }
                      }}
                      className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
                    >
                      <span
                        aria-hidden
                        className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
                      />
                      <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                        <div className="flex items-start justify-between gap-2.5">
                          <div className="flex-1 min-w-0">
                            <TitleTooltip text={row.title}>
                              <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                {row.title}
                              </h3>
                            </TitleTooltip>
                          </div>
                          <div className="shrink-0">
                            <FileText className="size-4 text-indigo-500 dark:text-indigo-400" />
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${DOC_TYPE_COLORS[row.documentType] ?? "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60"}`}
                          >
                            {formatDocType(row.documentType)}
                          </span>
                          <VersionChip version={row.currentVersion} />
                        </div>
                        <div className="mt-auto flex flex-col gap-3">
                          <MetaDates
                            created={row.createdAt}
                            updated={row.updatedAt}
                          />
                          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                            <StatusBadge status={variant} dot />
                            <div
                              className="flex items-center gap-1 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.hasFile ? (
                                <>
                                  <Tip label="Preview">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTablePreview(row.id);
                                      }}
                                      className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 text-[#64748b] dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    >
                                      <Eye className="size-4" />
                                    </button>
                                  </Tip>
                                  <Tip label="Download">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        triggerDownload(
                                          downloadGovernanceDocumentFile(row.id),
                                          row.fileName as string | null | undefined,
                                        );
                                      }}
                                      className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                    >
                                      <Download className="size-4" />
                                    </button>
                                  </Tip>
                                </>
                              ) : (
                                <span className="text-[10px] font-semibold text-[#94a3b8] dark:text-white/40 px-1">
                                  No file
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </>
      ) : (
        <>
          {/* Evidence Error banner */}
          {evidenceError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <span>{evidenceError}</span>
              <button
                onClick={fetchEvidence}
                className="ml-auto text-xs underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Evidence Search + Rows per page */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search Documents"
                value={evidenceSearch}
                onChange={(e) => {
                  setEvidenceSearch(e.target.value);
                  setEvPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm placeholder:d/50 outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all"
              />
            </div>
            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
              <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
              <label className="text-sm d whitespace-nowrap">
                Rows per page
              </label>
              <Dropdown
                value={String(evPageSize)}
                onChange={(v) => {
                  setEvPageSize(Number(v));
                  setEvPage(1);
                }}
                options={getPageSizeOptions(viewMode).map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>

          {/* Evidence Table with built-in pagination */}
          <DataTable
            columns={
              [
                ...getEvidenceColumns(evPage, evPageSize).map((col) => {
                  if (col.key === "sno") {
                    return {
                      ...col,
                      render: (row: EvidenceRow) => (
                        <span className="text-xs font-medium d">
                          {(evOriginalIndexMap.get(row.id) ?? 0) + 1}
                        </span>
                      ),
                    };
                  }
                  const evSortableLabels: Record<string, string> = {
                    title: "Title",
                    createdAt: "Created On",
                    updatedAt: "Updated On",
                  };
                  if (col.key in evSortableLabels) {
                    return {
                      ...col,
                      header: (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEvSort(col.key);
                          }}
                          className="inline-flex items-center whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                        >
                          {evSortableLabels[col.key]}
                          <EvSortIcon col={col.key} />
                        </button>
                      ),
                    };
                  }
                  return col;
                }),
                {
                  key: "id",
                  header: "Action",
                  headerClassName: "text-center",
                  render: (row) => (
                    <div className="flex items-center justify-center gap-0.5">
                      <Tip label="Edit">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditEvidenceModal(row);
                          }}
                          className="p-1.5 rounded-md hover:bg-accent d hover:text-foreground transition-all"
                        >
                          <Pencil className="size-4" />
                        </button>
                      </Tip>
                      {row.hasFile && (
                        <>
                          <Tip label="Preview">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewEvidenceRow(row);
                              }}
                              className="p-1.5 rounded-md hover:bg-accent d hover:text-foreground transition-all"
                            >
                              <Eye className="size-4" />
                            </button>
                          </Tip>
                          <Tip label="Download">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerDownload(
                                  downloadSupportingEvidenceFile(row.id),
                                  row.fileName,
                                );
                              }}
                              className="p-1.5 rounded-md hover:bg-accent d hover:text-foreground transition-all"
                            >
                              <Download className="size-4" />
                            </button>
                          </Tip>
                        </>
                      )}
                    </div>
                  ),
                },
              ] as Column<EvidenceRow & Record<string, unknown>>[]
            }
            data={paginatedEvidence}
            rowKey={(r) => r.id}
            onRowClick={handleSelectEvidence}
            loading={evidenceLoading}
            emptyText="No supporting evidence found"
            page={evPage - 1}
            pageSize={evPageSize}
            totalItems={sortedEvidence.length}
            onPageChange={(p) => setEvPage(p + 1)}
            itemLabel="items"
            enableCardView
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            renderCard={(rows) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
                {rows.map((row) => {
                  const tags = row.tags as string[];
                  return (
                    <div
                      key={row.id}
                      onClick={() => handleSelectEvidence(row)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectEvidence(row);
                        }
                      }}
                      className="group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:shadow-[0_12px_28px_-8px_rgba(79,70,229,0.28)] dark:hover:shadow-[0_0_28px_rgba(79,70,229,0.32)] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                    >
                      <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                        <div className="flex items-start justify-between gap-2.5">
                          <div className="flex-1 min-w-0">
                            <TitleTooltip text={row.title}>
                              <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                {row.title}
                              </h3>
                            </TitleTooltip>
                          </div>
                          <div className="shrink-0">
                            <FolderOpen className="size-4 text-indigo-500 dark:text-indigo-400" />
                          </div>
                        </div>
                        {tags && tags.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-medium text-indigo-600 dark:text-indigo-400"
                              >
                                <Tag className="size-2.5 flex-shrink-0" />
                                <span className="truncate max-w-[80px]">{tag}</span>
                              </span>
                            ))}
                            {tags.length > 3 && (
                              <span className="text-[10px] font-semibold text-[#94a3b8] dark:text-white/40">
                                +{tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        <div className="mt-auto flex flex-col gap-3">
                          <MetaDates
                            created={row.createdAt}
                            updated={row.updatedAt}
                          />
                          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                            <StatusBadge
                              status={row.hasFile ? "active" : "draft"}
                              label={row.hasFile ? "File attached" : "No file"}
                              dot
                            />
                            <div
                              className="flex items-center gap-1 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Tip label="Edit">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditEvidenceModal(row);
                                  }}
                                  className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                >
                                  <Pencil className="size-4" />
                                </button>
                              </Tip>
                              {row.hasFile && (
                                <>
                                  <Tip label="Preview">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPreviewEvidenceRow(row);
                                      }}
                                      className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 text-[#64748b] dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    >
                                      <Eye className="size-4" />
                                    </button>
                                  </Tip>
                                  <Tip label="Download">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        triggerDownload(
                                          downloadSupportingEvidenceFile(row.id),
                                          row.fileName,
                                        );
                                      }}
                                      className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                    >
                                      <Download className="size-4" />
                                    </button>
                                  </Tip>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      {mainContent}

      {/* ── Table File Preview Modal ── */}
      {tablePreviewDocId && (
        <PreviewModal
          url={`${previewGovernanceDocumentFile(tablePreviewDocId)}?v=${documents.find((d) => d.id === tablePreviewDocId)?.currentVersion ?? 1}&t=${tablePreviewToken}`}
          fileName={null}
          downloadUrl={downloadGovernanceDocumentFile(tablePreviewDocId)}
          onClose={closeTablePreview}
        />
      )}

      {/* ── Create Governance Document Modal (2-step) ── */}
      <Modal
        open={showCreateGov}
        onClose={dismissCreateGovWizard}
        title={createGovStep === "details" ? "Add Document" : "Upload Document"}
        description={
          createGovStep === "details"
            ? "Step 1 of 2 - Document details"
            : "Step 2 of 2 - Attach a file *"
        }
        onBack={
          createGovStep === "upload" && createGovStep2 === "PICKING"
            ? () => { setCreateGovStep("details"); setCreatedGovDocId(null); orphanGovDocIdRef.current = null; }
            : createGovStep === "upload" && createGovStep2 === "RESULTS"
              ? () => { setCreateGovStep2("PICKING"); setCreateGovValidationResult(null); }
              : undefined
        }
        size={createGovStep === "details" ? "lg" : "lg"}
        closable={
          createGovStep2 !== "CREATING" &&
          createGovStep2 !== "VALIDATING" &&
          createGovStep2 !== "UPLOADING"
        }
        footer={
          createGovStep === "details" ? (
            <ModalFooterButtons
              onCancel={dismissCreateGovWizard}
              onSubmit={handleCreateGovStep1}
              submitLabel="Next"
              submitDisabled={
                !newGovTitle.trim() ||
                newGovTitle.trim().length > TITLE_MAX_LENGTH ||
                !newGovType ||
                !newGovReviewCycle ||
                !newGovOrgId ||
                accessibleOrgs.length === 0 ||
                (newGovReviewCycle === "custom" &&
                  (!newGovCustomDays ||
                    parseInt(newGovCustomDays, 10) < 1 ||
                    parseInt(newGovCustomDays, 10) > DAYS_MAX))
              }
            />
          ) : (
            renderCreateGovStep2Footer()
          )
        }
      >
        {createGovStep === "details" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <FormField
                label="Title"
                required
                error={
                  newGovTitle.length > TITLE_MAX_LENGTH
                    ? `Title must be ${TITLE_MAX_LENGTH} characters or fewer`
                    : undefined
                }
                hint={undefined}
              >
                <FormInput
                  value={newGovTitle}
                  onChange={setNewGovTitle}
                  placeholder="e.g. Information Security Policy"
                />
              </FormField>
            </div>
            <FormField label="Document Type" required>
              <FormSelect
                options={DOCUMENT_TYPES}
                value={newGovType}
                onChange={(v) =>
                  setNewGovType(v as GovernanceDocumentType | "")
                }
                placeholder="Select document type"
              />
            </FormField>
            <FormField label="Organization" required>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin d" />
                  <span className="text-sm d">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <Ban className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                  <span className="d">({accessibleOrgs[0].shortName})</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    label: `${o.name} (${o.shortName})`,
                  }))}
                  value={newGovOrgId}
                  onChange={setNewGovOrgId}
                  placeholder="Select organization"
                />
              )}
            </FormField>
            <FormField label="Review Cycle" required>
              <FormSelect
                options={[
                  { value: "none", label: "No review required" },
                  { value: "90", label: "Quarterly" },
                  { value: "180", label: "Half-Yearly" },
                  { value: "365", label: "Annually" },
                  { value: "custom", label: "Custom" },
                ]}
                value={newGovReviewCycle}
                onChange={(v) =>
                  setNewGovReviewCycle(
                    v as "" | "none" | "90" | "180" | "365" | "custom",
                  )
                }
                placeholder="Select review cycle"
              />
            </FormField>
            {newGovReviewCycle === "custom" ? (
              <FormField
                label="Days"
                required
                error={
                  newGovCustomDays &&
                    (parseInt(newGovCustomDays, 10) < 1 ||
                      parseInt(newGovCustomDays, 10) > DAYS_MAX)
                    ? `Must be between 1 and ${DAYS_MAX} days`
                    : undefined
                }
                hint={!newGovCustomDays ? "e.g. 30, 60, 120" : undefined}
              >
                <FormInput
                  type="number"
                  value={newGovCustomDays}
                  onChange={setNewGovCustomDays}
                  placeholder="Enter days"
                  min={1}
                  max={DAYS_MAX}
                />
              </FormField>
            ) : (
              <div />
            )}
            <div className="sm:col-span-2">
              <FormField label="Description">
                <FormTextarea
                  value={newGovDescription}
                  onChange={setNewGovDescription}
                  placeholder="Brief description"
                  rows={3}
                />
              </FormField>
            </div>
          </div>
        ) : createGovStep2 === "PICKING" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-sm text-indigo-700 dark:text-indigo-400">
              <Upload className="size-4 flex-shrink-0" />
              <span className="min-w-0">
                Attach a PDF for{" "}
                <strong
                  className="inline-block max-w-[260px] truncate align-bottom"
                  title={newGovTitle}
                >
                  "{newGovTitle}"
                </strong>
                . We'll check it against the org's active rules before saving.
              </span>
            </div>
            <div>
              <label
                className={`flex flex-col items-center justify-center gap-2 px-4 ${createGovFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${createGovFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
              >
                {createGovFile ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                    <div className="flex items-start gap-2 min-w-0">
                      <FileText className="size-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-emerald-800 dark:text-emerald-300 font-medium break-all">
                          {createGovFile.name}
                        </span>
                        <span className="text-[11px] text-emerald-600/40 dark:text-emerald-400/40">
                          {formatFileSize(createGovFile.size)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCreateGovFile(null);
                        if (createGovFileInputRef.current)
                          createGovFileInputRef.current.value = "";
                      }}
                      className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="size-6 text-indigo-400 dark:text-indigo-300" />
                    <span className="text-sm font-semibold text-indigo-400 dark:text-indigo-300">
                      Click to select a  document
                    </span>
                    <span className="text-xs font-medium text-slate-300 dark:text-white/80">
                      PDF, DOC, DOCX · max 25 MB
                    </span>
                  </>
                )}
                <input
                  ref={createGovFileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
                  className="hidden"
                  onChange={(e) =>
                    handleCreateGovFileSelect(e.target.files?.[0] ?? null)
                  }
                />
              </label>
            </div>
          </div>
        ) : createGovStep2 === "CREATING" ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="size-8 animate-spin text-indigo-500" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">
                Creating document…
              </p>
              <p className="text-xs d">
                Setting up the DRAFT before validation runs.
              </p>
            </div>
          </div>
        ) : createGovStep2 === "VALIDATING" ? (
          <ValidationProgress
            fileName={createGovFile?.name}
            fileSize={
              createGovFile ? formatFileSize(createGovFile.size) : undefined
            }
          />
        ) : createGovStep2 === "UPLOADING" ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="size-8 animate-spin text-indigo-500" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">Uploading…</p>
              {createGovFile && (
                <p className="text-xs d">
                  <FileText className="inline size-3 mr-1 -mt-0.5" />
                  {createGovFile.name}
                </p>
              )}
            </div>
          </div>
        ) : createGovStep2 === "RESULTS" && createGovValidationResult ? (
          <ValidationResultPanel result={createGovValidationResult} />
        ) : createGovStep2 === "ERROR" ? (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
            <AlertTriangle className="size-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="space-y-0.5 min-w-0">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                {createGovValidationError?.title ?? "Error"}
              </p>
              <p className="text-sm text-red-700/90 dark:text-red-400/90">
                {createGovValidationError?.message ?? "Something went wrong."}
              </p>
            </div>
          </div>
        ) : (
          <div />
        )}
      </Modal>

      {/* ── Create Evidence Modal (2-step) ── */}
      <Modal
        open={showCreateEvidence}
        onClose={() => {
          setShowCreateEvidence(false);
          resetCreateEvidenceForm();
        }}
        title={
          createEvidenceStep === "details" ? "Add Evidence" : "Upload File"
        }
        description={
          createEvidenceStep === "details"
            ? "Step 1 of 2 - Evidence details"
            : "Step 2 of 2 - Attach a file (required)"
        }
        size={createEvidenceStep === "details" ? "lg" : "md"}
        closable={!creatingEvidence && !uploadingCreateEvidenceFile}
        footer={
          createEvidenceStep === "details" ? (
            <ModalFooterButtons
              onCancel={() => {
                setShowCreateEvidence(false);
                resetCreateEvidenceForm();
              }}
              onSubmit={handleCreateEvidenceStep1}
              submitLabel="Next"
              submitDisabled={
                creatingEvidence ||
                !newEvidenceTitle.trim() ||
                !!getTitleValidationError(newEvidenceTitle) ||
                !newEvidenceTags.trim() ||
                !newEvidenceOrgId ||
                accessibleOrgs.length === 0
              }
            />
          ) : (
            <button
              onClick={handleCreateEvidenceUpload}
              disabled={uploadingCreateEvidenceFile || !createEvidenceFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
            >
              {uploadingCreateEvidenceFile ? "Creating…" : "Upload & Finish"}
            </button>
          )
        }
      >
        {createEvidenceStep === "details" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <FormField label="Title" required>
                <FormInput
                  value={newEvidenceTitle}
                  onChange={setNewEvidenceTitle}
                  placeholder="e.g. SOC 2 Audit Report"
                  maxLength={EVIDENCE_TITLE_MAX}
                />
                {getTitleValidationError(newEvidenceTitle) ? (
                  <p className="text-xs text-red-500 mt-1">
                    {getTitleValidationError(newEvidenceTitle)}
                  </p>
                ) : (
                  newEvidenceTitle.length >= EVIDENCE_TITLE_MAX && (
                    <p className="text-xs text-amber-500 mt-1">
                      Maximum {EVIDENCE_TITLE_MAX} characters reached.
                    </p>
                  )
                )}
              </FormField>
            </div>
            <FormField label="Organization" required>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin d" />
                  <span className="text-sm d">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <Ban className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                  <span className="d">({accessibleOrgs[0].shortName})</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    label: `${o.name} (${o.shortName})`,
                  }))}
                  value={newEvidenceOrgId}
                  onChange={setNewEvidenceOrgId}
                  placeholder="Select organization"
                />
              )}
            </FormField>
            <FormField
              label="Tags"
              hint="Comma-separated tags for categorization"
              required
            >
              <FormInput
                value={newEvidenceTags}
                onChange={handleTagInputChange}
                placeholder="audit, soc2, 2024"
              />
              {tagSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tagSuggestions.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        const parts = newEvidenceTags.split(",");
                        parts[parts.length - 1] = tag;
                        setNewEvidenceTags(parts.join(", ") + ", ");
                        setTagSuggestions([]);
                      }}
                      className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </FormField>
            <div className="sm:col-span-2">
              <FormField label="Description">
                <FormTextarea
                  value={newEvidenceDescription}
                  onChange={setNewEvidenceDescription}
                  placeholder="Brief description"
                  rows={3}
                />
              </FormField>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-sm text-indigo-700 dark:text-indigo-400">
              <Upload className="size-4 flex-shrink-0" />
              <span>
                Upload a file for <strong>"{newEvidenceTitle}"</strong> to
                complete creation.
              </span>
            </div>
            <FormField label="Upload File" hint="Max size: 25 MB">
              <label
                className={`flex flex-col items-center justify-center gap-2 px-4 ${createEvidenceFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${createEvidenceFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
              >
                {createEvidenceFile ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-emerald-800 dark:text-emerald-300 font-medium truncate">
                        {createEvidenceFile.name}
                      </span>
                      <span className="text-[12px] text-emerald-600/60 flex-shrink-0">
                        {formatFileSize(createEvidenceFile.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCreateEvidenceFile(null);
                        if (createEvidenceFileInputRef.current)
                          createEvidenceFileInputRef.current.value = "";
                      }}
                      className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="size-5 text-indigo-500 dark:text-indigo-400" />
                    <span className="text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                      Click to select a file
                    </span>
                    <span className="text-[10px] text-[#475569] dark:text-white/60">
                      PDF, DOCX, images, and other formats supported
                    </span>
                  </>
                )}
                <input
                  ref={createEvidenceFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) =>
                    setCreateEvidenceFile(e.target.files?.[0] ?? null)
                  }
                />
              </label>
            </FormField>
          </div>
        )}
      </Modal>

      {/* ── Edit Evidence Modal ── */}
      <Modal
        open={showEditEvidenceModal}
        onClose={() => {
          setShowEditEvidenceModal(false);
          setEditingEvidenceId(null);
        }}
        title="Edit Evidence"
        size="lg"
        closable={!savingEvidenceEdit}
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowEditEvidenceModal(false);
              setEditingEvidenceId(null);
            }}
            onSubmit={handleSaveEvidenceFromModal}
            submitLabel={savingEvidenceEdit ? "Saving…" : "Save Changes"}
            submitDisabled={
              savingEvidenceEdit ||
              !editEvidenceTitle.trim() ||
              !!getTitleValidationError(editEvidenceTitle)
            }
          />
        }
      >
        <div className="grid grid-cols-1 gap-4">
          <FormField label="Title" required>
            <FormInput
              value={editEvidenceTitle}
              onChange={setEditEvidenceTitle}
              placeholder="e.g. SOC 2 Audit Report"
              maxLength={EVIDENCE_TITLE_MAX}
            />
            {getTitleValidationError(editEvidenceTitle) ? (
              <p className="text-xs text-red-500 mt-1">
                {getTitleValidationError(editEvidenceTitle)}
              </p>
            ) : (
              editEvidenceTitle.length >= EVIDENCE_TITLE_MAX && (
                <p className="text-xs text-amber-500 mt-1">
                  Maximum {EVIDENCE_TITLE_MAX} characters reached.
                </p>
              )
            )}
          </FormField>
          <FormField label="Description">
            <FormTextarea
              value={editEvidenceDescription}
              onChange={setEditEvidenceDescription}
              placeholder="Brief description"
              rows={3}
            />
          </FormField>
          <FormField
            label="Tags"
            hint="Comma-separated tags for categorization"
          >
            <FormInput
              value={editEvidenceTags}
              onChange={setEditEvidenceTags}
              placeholder="audit, soc2, 2024"
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Preview Evidence Modal ── */}
      {previewEvidenceRow && previewEvidenceRow.hasFile && (
        <PreviewModal
          url={previewSupportingEvidenceFile(previewEvidenceRow.id)}
          fileName={previewEvidenceRow.fileName}
          downloadUrl={downloadSupportingEvidenceFile(previewEvidenceRow.id)}
          onClose={() => setPreviewEvidenceRow(null)}
        />
      )}

      {/* ── Lifecycle Action Modal ── */}
      <Modal
        open={showLifecycleModal}
        onClose={() => setShowLifecycleModal(false)}
        title={lifecycleActionLabel(lifecycleAction)}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowLifecycleModal(false)}
            onSubmit={handleLifecycleAction}
            submitLabel={
              lifecycleProcessing
                ? "Processing…"
                : lifecycleActionLabel(lifecycleAction)
            }
            submitDisabled={
              lifecycleProcessing ||
              ((lifecycleAction === "reject" || lifecycleAction === "retire") &&
                !lifecycleReason.trim())
            }
          />
        }
      >
        <p className="text-sm text-foreground">
          {lifecycleAction === "submit" &&
            "Submit this document for review. It will move from Draft to In Review."}
          {lifecycleAction === "approve" &&
            "Approve this document. It will move to Approved status, ready to be published."}
          {lifecycleAction === "publish" &&
            "Publish this document. It will go live and the review cycle will begin."}
          {lifecycleAction === "reject" &&
            "Reject this document. It will return to Draft status with a rejection reason."}
          {lifecycleAction === "new-version" &&
            "Create a new draft version of this published document."}
          {lifecycleAction === "retire" &&
            "Withdraw this document. It will no longer be active and cannot be restored."}
        </p>
        {lifecycleAction === "reject" && (
          <FormField label="Reason" required>
            <FormTextarea
              placeholder="Reason for rejection…"
              value={lifecycleReason}
              onChange={setLifecycleReason}
              rows={3}
            />
          </FormField>
        )}
        {lifecycleAction === "retire" && (
          <FormField label="Reason" required>
            <FormTextarea
              placeholder="Reason for withdraw…"
              value={lifecycleReason}
              onChange={setLifecycleReason}
              rows={2}
            />
          </FormField>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmDialog
        open={deleteModalOpen}
        type={deleteTargetType === "governance" ? "Document" : "Evidence"}
        name={
          deleteTargetType === "governance"
            ? documents.find((d) => d.id === deleteTargetId)?.title || ""
            : evidence.find((e) => e.id === deleteTargetId)?.title || ""
        }
        deleting={!!deleting}
        showReason={false}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={() => confirmDelete()}
      />

      {/* ── File Upload Modal ── */}
      {uploadTargetType === "governance" ? (
        <GovernanceUploadModal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          documentId={selectedGovDoc?.id ?? ""}
          documentTitle={selectedGovDoc?.title ?? "Governance document"}
          orgName={selectedGovDoc?.owningOrgUnitName ?? undefined}
          onUploaded={() => {
            if (selectedGovDoc) fetchGovDocDetail(selectedGovDoc.id);
          }}
          onDiscarded={() => {
            // Backend soft-deleted the draft. Drop selection and refresh list.
            setShowUploadModal(false);
            setSelectedGovDoc(null);
            fetchGovDocuments();
          }}
        />
      ) : (
        <Modal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload File"
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowUploadModal(false)}
              onSubmit={handleUploadFile}
              submitLabel={uploading ? "Uploading…" : "Upload"}
              submitDisabled={uploading || !uploadFile}
            />
          }
        >
          <div className="space-y-3">
            <label
              className={`flex flex-col items-center justify-center gap-2 px-4 ${uploadFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${uploadFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
            >
              {uploadFile ? (
                <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-800 dark:text-emerald-300 font-medium truncate">
                      {uploadFile.name}
                    </span>
                    <span className="text-[12px] text-emerald-600/60 flex-shrink-0">
                      {formatFileSize(uploadFile.size)}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="size-5 text-indigo-500 dark:text-indigo-400" />
                  <span className="text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                    Click to select a file
                  </span>
                </>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ── Helper: lifecycle action label ── */
function lifecycleActionLabel(action: string): string {
  const labels: Record<string, string> = {
    submit: "Submit for Review",
    approve: "Approve",
    publish: "Publish",
    reject: "Reject",
    "new-version": "Add New Version",
    retire: "Withdraw",
  };
  return labels[action] ?? action;
}

/* ── Governance Document Detail Component ── */
function GovernanceDocumentDetail({
  doc,
  loading,
  editMode,
  editTitle,
  editDescription,
  editReviewCycle,
  editCustomDays,
  savingEdit,
  onBack,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onEditTitleChange,
  onEditDescriptionChange,
  onEditReviewCycleChange,
  onEditCustomDaysChange,
  onLifecycleAction,
  onUpload,
  onRemoveFile,
  onDelete,
  isEditable,
  history,
  historyLoading,
}: {
  doc: GovernanceDocumentResponse;
  loading: boolean;
  editMode: boolean;
  editTitle: string;
  editDescription: string;
  editReviewCycle: "none" | "90" | "180" | "365" | "custom";
  editCustomDays: string;
  savingEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditTitleChange: (v: string) => void;
  onEditDescriptionChange: (v: string) => void;
  onEditReviewCycleChange: (
    v: "none" | "90" | "180" | "365" | "custom",
  ) => void;
  onEditCustomDaysChange: (v: string) => void;
  onLifecycleAction: (action: string) => void;
  onUpload: () => void;
  onRemoveFile: () => void;
  onDelete: () => void;
  isEditable: boolean;
  history: HistoryEvent[];
  historyLoading: boolean;
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  return (
    <div className="space-y-6">
      {/* Breadcrumb + inline lifecycle */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm d hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Content Registry
        </button>
        <ChevronRight className="size-3.5 d" />
        <span className="text-sm font-medium">{doc.title}</span>
        <InlineLifecycle currentStatus={doc.status} />
      </div>

      {/* Title + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1>{doc.title}</h1>
            {doc.reviewDueSoon && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-xs font-bold text-amber-600 dark:text-amber-400 ring-1 ring-amber-200/60 dark:ring-amber-500/20">
                <AlertTriangle className="size-3" />
                Review Due Soon
              </span>
            )}
          </div>
          <p className="text-sm d mt-0.5">
            {formatDocType(doc.documentType)}
            {` · v${doc.currentVersion}`}
            {doc.status === "RETIRED" && " · Withdrawn"}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0 flex-wrap">
          {/* Edit button for DRAFT */}
          {isEditable && !editMode && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              <Pencil className="size-4" /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={onCancelEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <X className="size-4" /> Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={savingEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                {savingEdit ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}{" "}
                Save
              </button>
            </>
          )}

          {/* Lifecycle action buttons */}
          {doc.status === "DRAFT" && !editMode && (
            <>
              {!doc.currentVersionInfo?.hasFile && (
                <button
                  onClick={onUpload}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  <Upload className="size-4" /> Upload File
                </button>
              )}
              {doc.currentVersionInfo?.hasFile && (
                <button
                  onClick={() => onLifecycleAction("submit")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-md shadow-blue-500/20"
                >
                  <Send className="size-4" /> Submit for Review
                </button>
              )}
            </>
          )}
          {doc.status === "IN_REVIEW" && (
            <RBACAction module="document" action="approve">
              <>
                <button
                  onClick={() => onLifecycleAction("approve")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors shadow-md shadow-emerald-500/20"
                >
                  <ShieldCheck className="size-4" /> Approve
                </button>
                <button
                  onClick={() => onLifecycleAction("reject")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors shadow-md shadow-red-500/20"
                >
                  <XCircle className="size-4" /> Reject
                </button>
              </>
            </RBACAction>
          )}
          {doc.status === "APPROVED" && (
            <RBACAction module="document" action="approve">
              <button
                onClick={() => onLifecycleAction("publish")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-md shadow-blue-500/20"
              >
                <Send className="size-4" /> Publish
              </button>
            </RBACAction>
          )}
          {(doc.status === "PUBLISHED" || doc.status === "REVIEW_OVERDUE") && (
            <>
              <button
                onClick={() => onLifecycleAction("new-version")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <FilePlus className="size-4" /> New Version
              </button>
              <button
                onClick={() => onLifecycleAction("retire")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-800 text-white text-sm font-bold transition-colors shadow-md shadow-rose-500/20"
              >
                <Ban className="size-4" /> Withdraw
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <SkeletonList rows={3} />
      ) : (
        <>
          {/* Rejection banner */}
          {doc.rejectionReason && doc.status === "DRAFT" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
              <AlertTriangle className="size-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Document Rejected
                </p>
                <p className="text-sm text-red-600 dark:text-red-400/80 mt-0.5">
                  {doc.rejectionReason}
                </p>
                {doc.rejectedAt && (
                  <p className="text-xs text-red-500/70 mt-1">
                    Rejected on {formatDate(doc.rejectedAt)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Overview banner */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
            <div className="p-6">
              {editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Title" required>
                    <FormInput
                      value={editTitle}
                      onChange={onEditTitleChange}
                      placeholder="Document title"
                    />
                  </FormField>
                  <div className="flex gap-3">
                    <div
                      className={
                        editReviewCycle === "custom" ? "flex-1" : "w-full"
                      }
                    >
                      <FormField label="Review Cycle" required>
                        <FormSelect
                          options={[
                            { value: "none", label: "No review required" },
                            { value: "90", label: "Quarterly" },
                            { value: "180", label: "Half-Yearly" },
                            { value: "365", label: "Annually" },
                            { value: "custom", label: "Custom" },
                          ]}
                          value={editReviewCycle}
                          onChange={(v) =>
                            onEditReviewCycleChange(
                              v as "none" | "90" | "180" | "365" | "custom",
                            )
                          }
                        />
                      </FormField>
                    </div>
                    {editReviewCycle === "custom" && (
                      <div className="w-28">
                        <FormField label="Days" required>
                          <FormInput
                            type="number"
                            value={editCustomDays}
                            onChange={onEditCustomDaysChange}
                            placeholder="Days"
                          />
                        </FormField>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <FormField label="Description">
                      <FormTextarea
                        value={editDescription}
                        onChange={onEditDescriptionChange}
                        placeholder="Document description"
                        rows={3}
                      />
                    </FormField>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Status chips row */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${DOC_TYPE_COLORS[doc.documentType] ?? "bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/50"}`}
                    >
                      <FileText className="size-3" />
                      {formatDocType(doc.documentType)}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-white/[0.06] text-xs font-bold text-slate-600 dark:text-white/50 ring-1 ring-slate-200/60 dark:ring-white/[0.08] font-mono">
                      v{doc.currentVersion}
                    </span>
                    <StatusBadge
                      status={
                        mapLifecycleToVariant(doc.status) as Parameters<
                          typeof StatusBadge
                        >[0]["status"]
                      }
                      dot={false}
                    />
                  </div>

                  {/* Stat cards row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Created Date"
                      value={formatDate(doc.createdAt)}
                      color="slate"
                    />
                    <StatCard
                      icon={<Eye className="size-4" />}
                      label="Status"
                      value={(doc.status ?? "").replace(/_/g, " ").toLowerCase()}
                      color="indigo"
                      valueClassName="capitalize"
                    />
                    {doc.owningOrgUnitName && (
                      <StatCard
                        icon={<Building2 className="size-4" />}
                        label="Organization"
                        value={doc.owningOrgUnitName}
                        color="slate"
                        tooltip={doc.owningOrgUnitName}
                      />
                    )}
                    <StatCard
                      icon={<Calendar className="size-4" />}
                      label="Published Date"
                      value={
                        doc.publishedAt
                          ? formatDate(doc.publishedAt)
                          : "Not yet published"
                      }
                      dim={!doc.publishedAt}
                      color="emerald"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Last Updated Date"
                      value={formatDate(doc.updatedAt)}
                      color="slate"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Next Review Date"
                      value={
                        doc.reviewDate
                          ? formatDate(doc.reviewDate)
                          : doc.requiresReview
                            ? "Not scheduled"
                            : "No review required"
                      }
                      dim={!doc.reviewDate}
                      color={doc.status === "REVIEW_OVERDUE" ? "amber" : "sky"}
                    />
                  </div>

                  {/* Retirement reason */}
                  {doc.retirementReason && (
                    <div className="flex items-start gap-2 text-sm d">
                      <Ban className="size-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Retirement reason:</strong>{" "}
                        {doc.retirementReason}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Current Version File */}
          {doc.currentVersionInfo && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02]">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                  Current Version
                </h3>
                <div className="flex items-center gap-2">
                  {doc.currentVersionInfo.hasFile ? (
                    <>
                      <button
                        onClick={() => setShowPreview(true)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                        title="View file"
                      >
                        <Eye className="size-3" /> View
                      </button>
                      {isEditable && (
                        <>
                          <button
                            onClick={onUpload}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                            title="Replace file"
                          >
                            <Upload className="size-3" /> Replace
                          </button>
                          <button
                            onClick={onRemoveFile}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                            title="Delete file"
                          >
                            <Trash2 className="size-3" /> Delete
                          </button>
                        </>
                      )}
                    </>
                  ) : isEditable ? (
                    <button
                      onClick={onUpload}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                    >
                      <Upload className="size-3" /> Upload
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="p-4">
                {doc.currentVersionInfo.hasFile ? (
                  <div className="flex items-center gap-4">
                    <File className="size-10 d" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {doc.title}
                      </p>
                      <p className="text-xs d mt-0.5">
                        {doc.currentVersionInfo.fileName} · {formatFileSize(doc.currentVersionInfo.fileSize)}
                      </p>
                      {doc.description && (
                        <p className="text-xs d mt-1">{doc.description}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm d italic">No file uploaded yet</p>
                )}
              </div>
            </div>
          )}

          {/* Version History */}
          {doc.versions && doc.versions.length > 1 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                onClick={() => setExpandedVersions(!expandedVersions)}
                className="w-full flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:bg-[#eef2f7] dark:hover:bg-white/[0.04] transition-colors"
              >
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                  Version History
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    {doc.versions.length}
                  </span>
                </h3>
                <ChevronDown
                  className={`size-4 d transition-transform ${expandedVersions ? "rotate-180" : ""}`}
                />
              </button>
              {expandedVersions && (
                <div className="divide-y divide-border">
                  {doc.versions.map((ver) => (
                    <VersionRow
                      key={ver.versionNumber}
                      version={ver}
                      docId={doc.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity History */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setExpandedHistory(!expandedHistory)}
              className="w-full flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:bg-[#eef2f7] dark:hover:bg-white/[0.04] transition-colors"
            >
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <History className="size-4 text-indigo-500 dark:text-indigo-400" />
                Activity History
                {history.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    {history.length}
                  </span>
                )}
              </h3>
              <ChevronDown
                className={`size-4 d transition-transform ${expandedHistory ? "rotate-180" : ""}`}
              />
            </button>
            {expandedHistory && (
              <div className="p-4">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="size-5 animate-spin d" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="size-10 rounded-full bg-muted/50 flex items-center justify-center mb-2">
                      <History className="size-4 d" />
                    </div>
                    <p className="text-sm d">No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

                    <div className="space-y-4">
                      {history.map((event) => (
                        <div key={event.id} className="relative flex gap-3">
                          {/* Timeline dot */}
                          <div
                            className={`relative z-10 flex-shrink-0 size-6 rounded-full flex items-center justify-center ${event.eventType === "CREATED"
                                ? "bg-emerald-100 dark:bg-emerald-500/20"
                                : event.eventType === "SUBMITTED"
                                  ? "bg-blue-100 dark:bg-blue-500/20"
                                  : event.eventType === "APPROVED"
                                    ? "bg-emerald-100 dark:bg-emerald-500/20"
                                    : event.eventType === "REJECTED"
                                      ? "bg-red-100 dark:bg-red-500/20"
                                      : event.eventType === "PUBLISHED"
                                        ? "bg-emerald-100 dark:bg-emerald-500/20"
                                        : event.eventType === "RETIRED"
                                          ? "bg-slate-100 dark:bg-slate-500/20"
                                          : event.eventType ===
                                            "VERSION_CREATED"
                                            ? "bg-indigo-100 dark:bg-indigo-500/20"
                                            : "bg-slate-100 dark:bg-slate-500/20"
                              }`}
                          >
                            {event.eventType === "CREATED" && (
                              <Plus className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "SUBMITTED" && (
                              <Send className="size-3 text-blue-600 dark:text-blue-400" />
                            )}
                            {event.eventType === "APPROVED" && (
                              <CheckCircle className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "REJECTED" && (
                              <XCircle className="size-3 text-red-600 dark:text-red-400" />
                            )}
                            {event.eventType === "PUBLISHED" && (
                              <ShieldCheck className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "RETIRED" && (
                              <Ban className="size-3 text-slate-600 dark:text-slate-400" />
                            )}
                            {event.eventType === "VERSION_CREATED" && (
                              <FilePlus className="size-3 text-indigo-600 dark:text-indigo-400" />
                            )}
                            {![
                              "CREATED",
                              "SUBMITTED",
                              "APPROVED",
                              "REJECTED",
                              "PUBLISHED",
                              "RETIRED",
                              "VERSION_CREATED",
                            ].includes(event.eventType) && (
                                <History className="size-3 text-slate-600 dark:text-slate-400" />
                              )}
                          </div>

                          {/* Event content */}
                          <div className="flex-1 min-w-0 pb-4">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {event.eventDescription ||
                                    event.eventType.replace(/_/g, " ")}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex items-center gap-1.5 text-xs d">
                                    <User className="size-3" />
                                    <span>
                                      {getHistoryActorName(event, null)}
                                    </span>
                                  </div>
                                  {event.fromStatus && event.toStatus && event.fromStatus !== event.toStatus && (
                                    <div className="flex items-center gap-1 text-xs">
                                      <span className="px-1.5 py-0.5 rounded bg-muted/50 d font-medium">
                                        {event.fromStatus.replace(/_/g, " ")}
                                      </span>
                                      <ChevronRight className="size-3 d" />
                                      <span className="px-1.5 py-0.5 rounded bg-muted/50 d font-medium">
                                        {event.toStatus.replace(/_/g, " ")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <span className="flex-shrink-0 text-xs d whitespace-nowrap">
                                {formatDateTime(event.performedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview modal */}
      {showPreview && doc.currentVersionInfo?.hasFile && (
        <PreviewModal
          url={`${previewGovernanceDocumentFile(doc.id)}?v=${encodeURIComponent(
            [
              doc.currentVersionInfo.versionNumber,
              doc.currentVersionInfo.fileSize ?? "",
              doc.currentVersionInfo.fileName ?? "",
              doc.currentVersionInfo.createdAt ?? "",
            ].join("-"),
          )}`}
          fileName={doc.currentVersionInfo.fileName}
          downloadUrl={downloadGovernanceDocumentFile(doc.id)}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

/* ── Evidence Detail Component ── */
function EvidenceDetail({
  evidence,
  loading,
  editMode,
  editTitle,
  editDescription,
  editTags,
  savingEdit,
  onBack,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onEditTitleChange,
  onEditDescriptionChange,
  onEditTagsChange,
  onUpload,
  onDelete,
}: {
  evidence: SupportingEvidenceResponse;
  loading: boolean;
  editMode: boolean;
  editTitle: string;
  editDescription: string;
  editTags: string;
  savingEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditTitleChange: (v: string) => void;
  onEditDescriptionChange: (v: string) => void;
  onEditTagsChange: (v: string) => void;
  onUpload: () => void;
  onDelete: () => void;
}) {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm d hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Content Registry
        </button>
        <ChevronRight className="size-3.5 d" />
        <span className="text-sm d">Supporting Evidence</span>
        <ChevronRight className="size-3.5 d" />
        <span className="text-sm font-medium">{evidence.title}</span>
      </div>

      {/* Title + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>{evidence.title}</h1>
          <p className="text-sm d mt-0.5">Supporting Evidence</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {!editMode && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              <Pencil className="size-4" /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={onCancelEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <X className="size-4" /> Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={savingEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                {savingEdit ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}{" "}
                Save
              </button>
            </>
          )}
          <RBACAction module="evidence" action="delete">
            <button
              onClick={onDelete}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors shadow-md shadow-red-500/20"
            >
              <Trash2 className="size-4" /> Delete
            </button>
          </RBACAction>
        </div>
      </div>

      {loading ? (
        <SkeletonList rows={2} />
      ) : (
        <>
          {/* Overview */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
            <div className="p-6">
              {editMode ? (
                <div className="grid grid-cols-1 gap-5">
                  <FormField label="Title" required>
                    <FormInput
                      value={editTitle}
                      onChange={onEditTitleChange}
                      placeholder="Evidence title"
                    />
                  </FormField>
                  <FormField label="Description">
                    <FormTextarea
                      value={editDescription}
                      onChange={onEditDescriptionChange}
                      placeholder="Description"
                      rows={3}
                    />
                  </FormField>
                  <FormField label="Tags" hint="Comma-separated">
                    <FormInput
                      value={editTags}
                      onChange={onEditTagsChange}
                      placeholder="audit, soc2, 2024"
                    />
                  </FormField>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Tags */}
                  {evidence.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {evidence.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-xs font-medium text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-200/60 dark:ring-indigo-500/20"
                        >
                          <Tag className="size-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Description */}
                  {evidence.description && (
                    <p className="text-sm d leading-relaxed max-w-2xl">
                      {evidence.description}
                    </p>
                  )}

                  {/* Stat cards row — matches Governance Document detail's card design */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Created Date"
                      value={formatDate(evidence.createdAt)}
                      color="slate"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Last Updated Date"
                      value={formatDate(evidence.updatedAt)}
                      color="slate"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* File */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02]">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                File
              </h3>
              <div className="flex items-center gap-2">
                {evidence.hasFile ? (
                  <button
                    onClick={() => setShowPreview(true)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                    title="View file"
                  >
                    <Eye className="size-3" /> View
                  </button>
                ) : (
                  <button
                    onClick={onUpload}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                  >
                    <Upload className="size-3" /> Upload
                  </button>
                )}
              </div>
            </div>
            <div className="p-4">
              {evidence.hasFile ? (
                <div className="flex items-center gap-4">
                  <File className="size-10 d" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {evidence.fileName}
                    </p>
                    <p className="text-xs d">
                      {evidence.contentType} ·{" "}
                      {formatFileSize(evidence.fileSize)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm d italic">No file uploaded yet</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Preview modal */}
      {showPreview && evidence.hasFile && (
        <PreviewModal
          url={previewSupportingEvidenceFile(evidence.id)}
          fileName={evidence.fileName}
          downloadUrl={downloadSupportingEvidenceFile(evidence.id)}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

/* ── Version Row Component ── */
function VersionRow({
  version,
  docId,
}: {
  version: VersionInfo;
  docId: string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="font-mono font-bold text-sm text-foreground">
          {formatVersion(version.versionNumber)}
        </span>
        <span className="text-xs d">{formatDate(version.createdAt)}</span>
        {version.hasFile && (
          <span className="text-xs d">
            {version.fileName} ({formatFileSize(version.fileSize)})
          </span>
        )}
      </div>
      {version.hasFile && (
        <button
          onClick={() =>
            triggerDownload(
              downloadGovernanceDocumentVersionFile(
                docId,
                version.versionNumber,
              ),
              version.fileName,
            )
          }
          className="p-1.5 rounded-md border border-border hover:bg-accent transition-colors text-foreground"
          title="Download"
        >
          <Download className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ── Markdown Renderer ── */
function MarkdownRenderer({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
      {lines.map((line, i) => {
        if (/^### /.test(line))
          return (
            <h3
              key={i}
              className="text-base font-bold mt-4 mb-1 text-foreground"
            >
              {line.replace(/^### /, "")}
            </h3>
          );
        if (/^## /.test(line))
          return (
            <h2
              key={i}
              className="text-lg font-bold mt-5 mb-2 text-foreground border-b border-border pb-1"
            >
              {line.replace(/^## /, "")}
            </h2>
          );
        if (/^# /.test(line))
          return (
            <h1
              key={i}
              className="text-xl font-bold mt-6 mb-3 text-foreground border-b border-border pb-2"
            >
              {line.replace(/^# /, "")}
            </h1>
          );
        if (/^---+$/.test(line.trim()))
          return <hr key={i} className="border-border my-4" />;
        if (/^> /.test(line))
          return (
            <blockquote
              key={i}
              className="border-l-4 border-indigo-400 dark:border-indigo-500 pl-4 d italic my-2"
            >
              {line.replace(/^> /, "")}
            </blockquote>
          );
        if (/^(\s*[-*+] )/.test(line)) {
          const indent = line.match(/^(\s*)/)?.[1].length ?? 0;
          return (
            <li
              key={i}
              className={`list-disc text-sm text-foreground ${indent > 0 ? "ml-6" : "ml-4"}`}
            >
              {inlineFormat(line.replace(/^\s*[-*+] /, ""))}
            </li>
          );
        }
        if (/^\s*\d+\. /.test(line))
          return (
            <li key={i} className="list-decimal ml-4 text-sm text-foreground">
              {inlineFormat(line.replace(/^\s*\d+\. /, ""))}
            </li>
          );
        if (/^```/.test(line)) return null;
        if (line.trim() === "") return <div key={i} className="h-2" />;
        return (
          <p key={i} className="text-sm leading-relaxed text-foreground my-0.5">
            {inlineFormat(line)}
          </p>
        );
      })}
    </div>
  );
}

function inlineFormat(text: string): React.ReactNode {
  // Bold, italic, inline code
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (/^`[^`]+`$/.test(part))
      return (
        <code
          key={i}
          className="px-1 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-xs font-mono text-pink-600 dark:text-pink-400"
        >
          {part.slice(1, -1)}
        </code>
      );
    if (/^\*\*[^*]+\*\*$/.test(part))
      return (
        <strong key={i} className="font-bold">
          {part.slice(2, -2)}
        </strong>
      );
    if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });
}

/* ── Preview Modal Component ── */
/**
 * Returns a copy of the PDF blob with its /Title metadata set to `fileName`,
 * so the browser's built-in PDF viewer displays the real name instead of
 * "(anonymous)". Falls back to the original blob if anything goes wrong.
 */
async function withPdfTitle(blob: Blob, fileName: string | null): Promise<Blob> {
  if (!fileName) return blob;
  // Drop the extension for a cleaner viewer title (e.g. "Report" not "Report.pdf").
  const title = fileName.replace(/\.[^./\\]+$/, "") || fileName;
  try {
    const bytes = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(bytes, { updateMetadata: false });
    pdf.setTitle(title, { showInWindowTitleBar: true });
    const out = await pdf.save();
    // `out` is a Uint8Array — a valid BlobPart at runtime; cast for TS's
    // stricter ArrayBuffer-generic typing.
    return new Blob([out as BlobPart], { type: "application/pdf" });
  } catch {
    return blob;
  }
}

const PreviewModal = React.memo(function PreviewModal({
  url,
  fileName,
  downloadUrl,
  onClose,
}: {
  url: string;
  fileName: string | null;
  downloadUrl: string;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>("");
  const [mdText, setMdText] = useState<string | null>(null);
  const [cannotPreview, setCannotPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) showToast("error", "Unable to load preview");
  }, [error]);

  useEffect(() => {
    let revoke = "";
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setMdText(null);
    setCannotPreview(false);
    const token = useAuthStore.getState().getToken();

    // Sequential async/await keeps each step strongly typed (the previous
    // .then-chain returned a union of promises that TS couldn't narrow).
    const load = async () => {
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to load");
      const ct = res.headers.get("content-type") ?? "";
      setContentType(ct);
      // Extract filename from Content-Disposition or prop
      const disposition = res.headers.get("content-disposition") ?? "";
      const headerFileName =
        disposition
          .match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)?.[1]
          ?.trim() ?? "";
      const effectiveFileName = (fileName ?? headerFileName).toLowerCase();
      // Binary types we can render natively
      const isImage = ct.startsWith("image/");
      const isPdf = ct.includes("pdf");

      if (isImage || isPdf) {
        let outBlob = await res.blob();
        // For PDFs, embed a /Title so Chrome's native viewer shows the real
        // filename instead of "(anonymous)" (blob: URLs carry no name).
        if (isPdf) outBlob = await withPdfTitle(outBlob, fileName);
        const objUrl = URL.createObjectURL(outBlob);
        revoke = objUrl;
        setBlobUrl(objUrl);
        return;
      }

      // Everything else: read as text and try to detect/render
      const text = await res.text();
      // Detect markdown by content-type, filename extension, or content pattern
      const isMd =
        ct.includes("markdown") ||
        effectiveFileName.endsWith(".md") ||
        effectiveFileName.endsWith(".mdx") ||
        /^#{1,6} |^\*\*|^> |^```|^- \w|^\d+\. \w/.test(text.trimStart());
      if (isMd) {
        setMdText(text);
      } else if (
        ct.includes("text/plain") ||
        ct.includes("text/html") ||
        ct.includes("text/xml")
      ) {
        // Safe text types: render in iframe
        const blob = new Blob([text], { type: ct });
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        setBlobUrl(objUrl);
      } else {
        // Unsupported binary type (octet-stream etc.) - don't let browser download
        setCannotPreview(true);
      }
    };

    load()
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url, fileName]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-6xl h-[90vh] rounded-xl border border-border bg-card shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Eye className="size-4 text-indigo-500 dark:text-indigo-400" />
            <h3 className="text-sm font-bold text-foreground">Preview</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => triggerDownload(downloadUrl, fileName)}
              className="p-1.5 rounded-md border border-border hover:bg-accent transition-colors text-foreground"
              title="Download"
            >
              <Download className="size-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#f5f7fb] dark:bg-black/20 flex items-center justify-center min-h-0">
          {loading && (
            <div className="flex flex-col items-center gap-3 p-8">
              <Loader2 className="size-8 d animate-spin" />
              <p className="text-sm d">Loading preview…</p>
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <AlertTriangle className="size-12 text-red-400" />
              <p className="text-sm font-medium text-foreground">
                Failed to load preview
              </p>
              <p className="text-xs d">
                Use the download button to view the file.
              </p>
            </div>
          )}
          {cannotPreview && (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <AlertTriangle className="size-12 d" />
              <p className="text-sm font-medium text-foreground">
                Preview not available for this file type
              </p>
              <p className="text-xs d">
                Use the download button to view the file.
              </p>
            </div>
          )}
          {!loading && !error && !cannotPreview && mdText !== null && (
            <div className="w-full h-full overflow-auto p-6 bg-white dark:bg-[#0f0f1a]">
              <MarkdownRenderer content={mdText} />
            </div>
          )}
          {!loading && !error && !cannotPreview && blobUrl && (
            <>
              {contentType.startsWith("image/") && (
                <img
                  src={blobUrl}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                />
              )}
              {contentType.includes("pdf") && (
                <embed
                  src={blobUrl}
                  type="application/pdf"
                  className="w-full h-full"
                />
              )}
              {!contentType.startsWith("image/") &&
                !contentType.includes("pdf") && (
                  <iframe
                    src={blobUrl}
                    className="w-full h-full border-0 bg-white"
                    title="Preview"
                  />
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

/* ── Stat Card ── */
const statColors: Record<
  string,
  { icon: string; iconBg: string; bg: string; ring: string; border: string }
> = {
  indigo: {
    icon: "text-indigo-500 dark:text-indigo-400",
    iconBg: "bg-indigo-100/80 dark:bg-indigo-500/15",
    bg: "bg-indigo-50/40 dark:bg-indigo-500/[0.06]",
    ring: "ring-indigo-200/60 dark:ring-indigo-500/20",
    border: "border-l-indigo-400 dark:border-l-indigo-500",
  },
  sky: {
    icon: "text-sky-500 dark:text-sky-400",
    iconBg: "bg-sky-100/80 dark:bg-sky-500/15",
    bg: "bg-sky-50/40 dark:bg-sky-500/[0.06]",
    ring: "ring-sky-200/60 dark:ring-sky-500/20",
    border: "border-l-sky-400 dark:border-l-sky-500",
  },
  amber: {
    icon: "text-amber-500 dark:text-amber-400",
    iconBg: "bg-amber-100/80 dark:bg-amber-500/15",
    bg: "bg-amber-50/40 dark:bg-amber-500/[0.06]",
    ring: "ring-amber-200/60 dark:ring-amber-500/20",
    border: "border-l-amber-400 dark:border-l-amber-500",
  },
  emerald: {
    icon: "text-emerald-500 dark:text-emerald-400",
    iconBg: "bg-emerald-100/80 dark:bg-emerald-500/15",
    bg: "bg-emerald-50/40 dark:bg-emerald-500/[0.06]",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/20",
    border: "border-l-emerald-400 dark:border-l-emerald-500",
  },
  slate: {
    icon: "text-slate-400 dark:text-white/40",
    iconBg: "bg-slate-100/80 dark:bg-white/[0.06]",
    bg: "bg-slate-50/60 dark:bg-white/[0.03]",
    ring: "ring-slate-200/50 dark:ring-white/[0.08]",
    border: "border-l-slate-300 dark:border-l-white/20",
  },
};

function StatCard({
  icon,
  label,
  value,
  color = "slate",
  dim = false,
  tooltip,
  valueClassName = "",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  dim?: boolean;
  tooltip?: string;
  valueClassName?: string;
}) {
  const c = statColors[color] ?? statColors.slate;
  const valueEl = (
    <span
      className={`block leading-tight truncate ${dim ? "text-xs italic d/70" : "text-sm font-semibold text-foreground"} ${valueClassName}`}
    >
      {value}
    </span>
  );
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl ${c.bg} ring-1 ${c.ring} border-l-2 ${c.border} transition-colors`}
    >
      <div
        className={`flex-shrink-0 size-8 rounded-lg ${c.iconBg} flex items-center justify-center`}
      >
        <span className={c.icon}>{icon}</span>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] font-bold tracking-widest d">
          {label}
        </span>
        {tooltip ? (
          <Tip
            label={tooltip}
            className="min-w-0"
            contentClassName="max-w-xs break-words"
          >
            {valueEl}
          </Tip>
        ) : (
          valueEl
        )}
      </div>
    </div>
  );
}

/* ── Inline Lifecycle Stepper ── */
function InlineLifecycle({
  currentStatus,
}: {
  currentStatus: DocumentLifecycleStatus;
}) {
  // Main workflow: DRAFT → IN_REVIEW → APPROVED → PUBLISHED
  // Terminal statuses: REVIEW_OVERDUE (from PUBLISHED), RETIRED (terminal)
  const mainSteps = ["DRAFT", "IN_REVIEW", "APPROVED", "PUBLISHED"] as const;
  const stepLabels: Record<string, string> = {
    DRAFT: "Draft",
    IN_REVIEW: "In Review",
    APPROVED: "Approved",
    PUBLISHED: "Published",
    RETIRED: "Withdrawn",
  };

  const isMainStatus = mainSteps.includes(
    currentStatus as (typeof mainSteps)[number],
  );
  const activeIdx = isMainStatus
    ? mainSteps.indexOf(currentStatus as (typeof mainSteps)[number])
    : mainSteps.length;

  return (
    <div className="ml-3 flex items-center gap-1">
      {mainSteps.map((s, i) => {
        const isCurrent = s === currentStatus;
        const isPast = activeIdx >= 0 && i < activeIdx;
        return (
          <React.Fragment key={s}>
            <div
              className={`flex items-center gap-1 ${isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "d/40"}`}
            >
              <div
                className={`size-2 rounded-full ${isCurrent ? "bg-blue-500 ring-2 ring-blue-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/20"}`}
              />
              <span
                className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
              >
                {stepLabels[s]}
              </span>
            </div>
            {i < mainSteps.length - 1 && (
              <div
                className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/15"}`}
              />
            )}
          </React.Fragment>
        );
      })}
      {currentStatus === "RETIRED" && (
        <>
          <div className="w-4 h-px bg-muted-foreground/15" />
          <div className="flex items-center gap-1">
            <div className="size-2 rounded-full bg-slate-500" />
            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
              Withdrawn
            </span>
          </div>
        </>
      )}
    </div>
  );
}
